import { useState, useEffect, useCallback, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { surveyAPI } from '../services/api'
import {
  surveyToFinancialDraft,
  initPctMtFlags,
  recalcFinancialDraftTotals,
  sumOthersLineAmounts,
} from '../utils/invoiceFinancialCalculations'

/** Maps invoice workspace row -> Survey financial draft field key. */
const WORKSPACE_AMOUNT_KEYS = {
  survey_fee: 'survey_fee',
  travel_expenses: 'expenses',
  incentive: 'recovery',
  others: 'others',
}

/** Charge lines shown on the invoice (labels align with Survey financial details). */
const CHARGE_LINE_KEYS = ['survey_fee', 'travel_expenses', 'incentive', 'others']

/** Standard rows — Others uses `invoice_meta.extra_lines` as a breakdown rolled up into Survey.others. */
const CORE_CHARGE_LINE_KEYS = CHARGE_LINE_KEYS.filter((k) => k !== 'others')

const Invoice = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const [invoiceData, setInvoiceData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [selectedItems, setSelectedItems] = useState({
    survey_fee: false,
    travel_expenses: false,
    incentive: false,
    others: false,
    discount: false,
    tax: false,
    recovery: false
  })
  // State for edited invoice items
  const [editedItems, setEditedItems] = useState({})
  // State for edited invoice details
  const [editedDetails, setEditedDetails] = useState({})
  // State for edited recovery table
  const [editedRecovery, setEditedRecovery] = useState({})
  // State for edited discount (stored as string)
  const [editedDiscount, setEditedDiscount] = useState(null)
  // State for edited total amount due
  const [editedTotalAmount, setEditedTotalAmount] = useState(null)
  const addressSpanRef = useRef(null)
  const [, setAddressHeight] = useState(null)
  const titleMeasureRef = useRef(null)
  const [titleWidth, setTitleWidth] = useState(null)
  // Currency state - default to USD, persist across edit mode toggles
  const [currency, setCurrency] = useState('USD')
  // State for empty rows
  const [emptyRows, setEmptyRows] = useState([])

  const [saveStatus, setSaveStatus] = useState('idle') // idle | saving | saved | error
  const [saveError, setSaveError] = useState(null)
  const dirtyRef = useRef(false)
  const suspendAutoSaveRef = useRef(false)
  const pctDisabledRef = useRef(false)
  const mtPriceDisabledRef = useRef(false)

  const [financialDraft, setFinancialDraft] = useState(null)
  const [pctDisabled, setPctDisabled] = useState(false)
  const [mtPriceDisabled, setMtPriceDisabled] = useState(false)

  const markDirty = () => {
    dirtyRef.current = true
  }

  const loadInvoiceData = useCallback(async () => {
    try {
      suspendAutoSaveRef.current = true
      setLoading(true)
      const response = await surveyAPI.getInvoiceData(id)
      if (response.data.success) {
        setInvoiceData(response.data)
        const meta = response.data.invoice_meta || response.data.survey?.invoice_meta || {}
        const lineDesc = meta.line_descriptions || {}

        // Initialize edited items with original values as strings
        const items = response.data.available_items || {}
        const initialEdited = {}
        const ensuredItems = {
          ...items,
          others: items.others ?? { description: 'Others', amount: 0 },
        }

        Object.keys(ensuredItems).forEach(key => {
          const amount = ensuredItems[key].amount
          let amountStr = '0.00'
          if (amount !== null && amount !== undefined) {
            if (typeof amount === 'number') {
              amountStr = amount.toFixed(2)
            } else {
              const numValue = parseFloat(amount)
              if (!isNaN(numValue)) {
                amountStr = numValue.toFixed(2)
              } else {
                amountStr = String(amount)
              }
            }
          }

          let description = String(ensuredItems[key].description || '')
          if (key === 'survey_fee' && response.data.survey?.survey_type) {
            description = `${description} (${response.data.survey.survey_type})`
          }
          if (lineDesc[key]) {
            description = String(lineDesc[key])
          }

          initialEdited[key] = {
            description: description,
            amount: amountStr
          }
        })

        let breakdownRows = Array.isArray(meta.extra_lines)
          ? meta.extra_lines.map((row, idx) => ({
              id: row.id != null ? row.id : `loaded-${idx}-${Date.now()}`,
              description: row.description != null ? String(row.description) : '',
              amount: row.amount != null ? String(row.amount) : '0.00',
              selected: row.selected !== false,
            }))
          : []

        if (breakdownRows.length === 0) {
          const legacyOthers = parseFloat(String(initialEdited.others?.amount ?? '0'))
          if (Number.isFinite(legacyOthers) && legacyOthers !== 0) {
            breakdownRows = [
              {
                id: `invoice-legacy-others-${Date.now()}`,
                description: '',
                amount: legacyOthers.toFixed(2),
                selected: true,
              },
            ]
          }
        }

        let othersBreakdownSum = null
        if (breakdownRows.length > 0) {
          othersBreakdownSum = sumOthersLineAmounts(breakdownRows, { onlySelected: true })
          initialEdited.others = {
            ...initialEdited.others,
            amount: othersBreakdownSum.toFixed(2),
          }
        }

        setEditedItems(initialEdited)
        setEmptyRows(breakdownRows)

        const defaultSel = {
          survey_fee: false,
          travel_expenses: false,
          incentive: false,
          others: false,
          discount: false,
          tax: false,
          recovery: false
        }
        setSelectedItems({
          ...defaultSel,
          ...(meta.selectedItems || {}),
        })

        if (meta.currency) {
          setCurrency(meta.currency)
        }

        setEditedDiscount(null)

        const lo = meta.label_overrides || {}
        const labelState = {}
        if (lo.discount_label !== undefined) labelState.discount_label = lo.discount_label
        if (lo.tax_label !== undefined) labelState.tax_label = lo.tax_label
        if (lo.total_label !== undefined) labelState.total_label = lo.total_label
        setEditedDetails(Object.keys(labelState).length ? labelState : {})

        if (meta.total_override != null && meta.total_override !== '') {
          setEditedTotalAmount(String(meta.total_override))
        } else {
          setEditedTotalAmount(null)
        }

        setEditedRecovery({})

        const surv = response.data.survey
        let fd = surveyToFinancialDraft(surv)
        if (othersBreakdownSum != null) {
          fd = recalcFinancialDraftTotals({
            ...fd,
            others: othersBreakdownSum.toFixed(2),
          })
        }
        setFinancialDraft(fd)
        const pctFlags = initPctMtFlags(surv)
        setPctDisabled(pctFlags.pctDisabled)
        setMtPriceDisabled(pctFlags.mtPriceDisabled)
        pctDisabledRef.current = pctFlags.pctDisabled
        mtPriceDisabledRef.current = pctFlags.mtPriceDisabled

        dirtyRef.current = false
        setSaveStatus('idle')
        setSaveError(null)
      } else {
        setError(response.data.message || 'Failed to load invoice data')
      }
    } catch (err) {
      console.error('Error loading invoice:', err)
      setError(err.response?.data?.message || 'Failed to load invoice data')
    } finally {
      setLoading(false)
      setTimeout(() => {
        suspendAutoSaveRef.current = false
      }, 500)
    }
  }, [id])

  useEffect(() => {
    if (id) {
      loadInvoiceData()
    }
  }, [id, loadInvoiceData])

  useEffect(() => {
    if (saveStatus !== 'saved') return undefined
    const t = setTimeout(() => setSaveStatus('idle'), 2500)
    return () => clearTimeout(t)
  }, [saveStatus])

  // Capture address block height when invoice data renders
  useEffect(() => {
    if (!addressSpanRef.current) return undefined
    const height = addressSpanRef.current.offsetHeight
    setAddressHeight(height)
    return undefined
  }, [invoiceData])

  // Measure invoice title width for layout
  useEffect(() => {
    if (!invoiceData) return undefined
    const tempSpan = document.createElement('span')
    tempSpan.className = 'invoice-title mb-0 text-left'
    tempSpan.style.position = 'absolute'
    tempSpan.style.visibility = 'hidden'
    tempSpan.style.whiteSpace = 'nowrap'
    tempSpan.style.fontSize = '2.5rem'
    tempSpan.style.fontWeight = 'bold'
    tempSpan.style.fontFamily = window.getComputedStyle(document.body).fontFamily
    const titleText = getDetailValue('invoice_title') || 'INVOICE'
    tempSpan.textContent = titleText
    document.body.appendChild(tempSpan)
    const width = tempSpan.offsetWidth
    document.body.removeChild(tempSpan)
    setTitleWidth(Math.max(width + 20, 150))
    return undefined
  // getDetailValue is derived from invoiceData + edits; invoiceData deps are sufficient for initial measure
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceData])


  const handleItemEdit = (itemKey, field, value) => {
    if (itemKey === 'others' && field === 'amount') {
      return
    }
    markDirty()
    setEditedItems((prev) => ({
      ...prev,
      [itemKey]: {
        ...prev[itemKey],
        [field]: value,
      },
    }))
    if (financialDraft && field === 'amount') {
      const draftKey = WORKSPACE_AMOUNT_KEYS[itemKey]
      if (draftKey) {
        setFinancialDraft((prevDraft) =>
          prevDraft ? recalcFinancialDraftTotals({ ...prevDraft, [draftKey]: value }) : prevDraft
        )
      }
    }
  }

  const handleDiscountWorkspaceChange = (value) => {
    markDirty()
    setEditedDiscount(null)
    setFinancialDraft((prev) =>
      prev ? recalcFinancialDraftTotals({ ...prev, discount: value }) : prev
    )
  }

  const handleDetailEdit = (field, value) => {
    markDirty()
    setEditedDetails(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const handleRecoveryEdit = (field, value) => {
    markDirty()
    setEditedRecovery(prev => ({
      ...prev,
      [field]: value // Store as string, no parsing
    }))
  }

  /** Nested rows under Others -> stored in invoice_meta.extra_lines; total is Survey.others. */
  const addOthersBreakdownLine = () => {
    if (!selectedItems.others) return
    markDirty()
    setEmptyRows((prev) => {
      const newId = Date.now()
      if (prev.length > 0) {
        return [...prev, { id: newId, description: '', amount: '0.00', selected: true }]
      }
      const seeded = parseFloat(getItemValue('others', 'amount')) || 0
      return [
        {
          id: newId,
          description: '',
          amount: seeded > 0 ? seeded.toFixed(2) : '0.00',
          selected: true,
        },
      ]
    })
  }

  const removeOthersBreakdownLine = (lineId) => {
    markDirty()
    setEmptyRows((prev) => prev.filter((row) => row.id !== lineId))
  }

  const handleEmptyRowEdit = (id, field, value) => {
    markDirty()
    setEmptyRows((prev) => prev.map((row) => (row.id === id ? { ...row, [field]: value } : row)))
  }

  const handleEmptyRowToggle = (lineId) => {
    markDirty()
    setEmptyRows((prev) =>
      prev.map((row) =>
        row.id === lineId
          ? { ...row, selected: row.selected === false ? true : false }
          : row
      )
    )
  }

  const formatDate = (dateString) => {
    if (!dateString) return '-'
    
    // Parse date string as date components to avoid timezone conversion issues
    // Backend sends dates as YYYY-MM-DD strings which should be timezone-neutral
    let year, month, day
    
    if (typeof dateString === 'string') {
      // Extract YYYY-MM-DD from string (handle both pure date strings and ISO datetime strings)
      const dateMatch = dateString.match(/^(\d{4})-(\d{2})-(\d{2})/)
      if (dateMatch) {
        year = parseInt(dateMatch[1], 10)
        month = parseInt(dateMatch[2], 10) - 1 // Month is 0-indexed
        day = parseInt(dateMatch[3], 10)
      } else {
        // Fallback: parse as Date and extract components in local timezone
        const date = new Date(dateString)
        year = date.getFullYear()
        month = date.getMonth()
        day = date.getDate()
      }
    } else {
      // If it's already a Date object, extract components in local timezone
      year = dateString.getFullYear()
      month = dateString.getMonth()
      day = dateString.getDate()
    }
    
    // Create a date object with the parsed components and format it
    // Using local date constructor avoids timezone conversion
    const date = new Date(year, month, day)
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric'
    })
  }

  const formatCurrency = (value) => {
    if (value === null || value === undefined || value === 0) return '-'
    const numValue = parseFloat(value).toFixed(2)
    // Dollar symbol at beginning, Euro symbol at end
    if (currency === 'EUR') {
      return `${numValue}€`
    } else {
      return `$${numValue}`
    }
  }

  const formatChargeAlways = (value) => {
    const raw = parseFloat(value)
    const n = (Number.isFinite(raw) ? raw : 0).toFixed(2)
    return currency === 'EUR' ? `${n}€` : `$${n}`
  }

  const formatFloat = (value, decimals = 3) => {
    if (value === null || value === undefined) return '-'
    return parseFloat(value).toFixed(decimals)
  }

  const roundTo2 = (value) => {
    // Avoid common floating point issues (e.g. 0.1 * 0.2)
    const n = Number(value)
    if (!Number.isFinite(n)) return 0
    return Math.round((n + Number.EPSILON) * 100) / 100
  }

  // Calculate input width based on content (reserved for future use)
  // eslint-disable-next-line no-unused-vars
  const _getInputWidth = (value, minWidth = 50, maxWidth = 500) => {
    if (!value) return minWidth
    // Estimate width: ~8px per character for most fonts, add padding
    const charWidth = 8
    const padding = 20
    const calculatedWidth = Math.max(minWidth, (value.toString().length * charWidth) + padding)
    return Math.min(calculatedWidth, maxWidth)
  }

  // Measure actual rendered width of text with given styling
  const measureTextWidth = (text, styles = {}) => {
    const tempSpan = document.createElement('span')
    Object.assign(tempSpan.style, {
      position: 'absolute',
      visibility: 'hidden',
      whiteSpace: 'nowrap',
      fontFamily: window.getComputedStyle(document.body).fontFamily,
      ...styles
    })
    tempSpan.textContent = text || ''
    document.body.appendChild(tempSpan)
    const width = tempSpan.offsetWidth
    document.body.removeChild(tempSpan)
    return width
  }

  // Auto-resize textarea based on content - maintain original height exactly (reserved for future use)
  // eslint-disable-next-line no-unused-vars
  const _handleTextareaResize = (e) => {
    const textarea = e.target
    // Reset height to auto to get accurate scrollHeight
    textarea.style.height = 'auto'
    // Use scrollHeight to match content exactly (in pixels)
    const calculatedHeight = Math.max(textarea.scrollHeight, 24) // Minimum 24px for single line
    textarea.style.height = `${calculatedHeight}px`
  }
  
  // Calculate initial textarea height based on content - match original text exactly (reserved for future use)
  // eslint-disable-next-line no-unused-vars
  const _getTextareaHeight = (text) => {
    if (!text) return '24px' // Single line height in pixels
    // Count actual lines (newlines + 1)
    const lineCount = (text.match(/\n/g) || []).length + 1
    // Line height is 24px, padding is 8px (4px top + 4px bottom)
    // For single line: 24px (line) + 8px (padding) = 32px
    // For each additional line: add 24px
    const baseHeight = 32 // single line with padding
    const additionalLines = lineCount - 1
    const calculatedHeight = baseHeight + (additionalLines * 24)
    return `${calculatedHeight}px`
  }
  

  const calculateTotals = () => {
    if (!invoiceData) return { subtotal: 0, tax: 0, discount: 0, total: 0 }

    let subtotal = 0
    const originals = invoiceData.available_items || {}
    const mergedAvail = {
      ...originals,
      others: originals.others ?? { description: 'Others', amount: 0 },
    }
    const itemKeys = new Set([
      ...Object.keys(mergedAvail),
      ...Object.keys(editedItems),
    ])
    const items = {}
    itemKeys.forEach((key) => {
      const o = mergedAvail[key] || {}
      const e = editedItems[key] || {}
      items[key] = {
        description: e.description !== undefined ? e.description : o.description,
        amount:
          e.amount !== undefined
            ? e.amount
            : o.amount !== undefined && o.amount !== null
              ? o.amount
              : 0,
      }
    })
    
    // Parse string values to numbers for calculation
    if (selectedItems.survey_fee && items.survey_fee) {
      const amount = parseFloat(items.survey_fee.amount) || 0
      subtotal += amount
    }
    if (selectedItems.travel_expenses && items.travel_expenses) {
      const amount = parseFloat(items.travel_expenses.amount) || 0
      subtotal += amount
    }
    if (selectedItems.incentive && items.incentive) {
      const amount = parseFloat(items.incentive.amount) || 0
      subtotal += amount
    }
    if (selectedItems.others && items.others) {
      subtotal += sumOthersLineAmounts(emptyRows, { onlySelected: true })
    }
    
    const discountSrc =
      editedDiscount !== null && editedDiscount !== undefined
        ? editedDiscount
        : (financialDraft?.discount ?? invoiceData.discount)
    const discount = selectedItems.discount ? (parseFloat(discountSrc) || 0) : 0
    // Tax is 5% of subtotal (before discount) - matching backend
    const taxRate = selectedItems.tax ? 5 : 0
    const tax = subtotal * (taxRate / 100)
    const total = subtotal - discount
    
    return { subtotal, tax, discount, total }
  }

  const getItemValue = (itemKey, field) => {
    // Always use edited values if they exist (regardless of edit mode)
    // This allows edited values to persist in view mode (temporary changes only)
    if (editedItems[itemKey] && editedItems[itemKey][field] !== undefined) {
      const editedValue = editedItems[itemKey][field]
      if (field === 'amount') {
        // Preserve decimal format for amounts
        const numValue = parseFloat(editedValue)
        if (!isNaN(numValue)) {
          return numValue.toFixed(2)
        }
        return String(editedValue || '0.00')
      }
      return String(editedValue || '')
    }
    // Fall back to original value if no edits exist
    const value =
      invoiceData?.available_items?.[itemKey]?.[field] ??
      (itemKey === 'others'
        ? (field === 'description' ? 'Others' : field === 'amount' ? 0 : '')
        : undefined)
    if (field === 'amount') {
      // Preserve decimal format when not in edit mode (for display)
      if (value !== null && value !== undefined) {
        if (typeof value === 'number') {
          return value.toFixed(2)
        } else {
          // If string, try to parse and format
          const numValue = parseFloat(value)
          if (!isNaN(numValue)) {
            return numValue.toFixed(2)
          }
          return String(value)
        }
      }
      return '0.00'
    }
    return String(value || '')
  }

  /** Keep invoice / financial draft Others amount equal to the sum of breakdown rows (persisted as Survey.others). */
  useEffect(() => {
    const sum = sumOthersLineAmounts(emptyRows, { onlySelected: true })
    const s = sum.toFixed(2)
    setEditedItems((prev) => ({
      ...prev,
      others: {
        ...(prev.others || { description: 'Others', amount: '0.00' }),
        amount: s,
      },
    }))
    setFinancialDraft((prev) =>
      prev ? recalcFinancialDraftTotals({ ...prev, others: s }) : prev
    )
  }, [emptyRows])

  const getDetailValue = (field) => {
    // Always use edited values if they exist (regardless of edit mode)
    if (editedDetails[field] !== undefined) {
      return String(editedDetails[field] || '')
    }
    const meta = invoiceData?.invoice_meta || invoiceData?.survey?.invoice_meta || {}
    // Handle nested fields
    const fieldMap = {
      'client_name': invoiceData?.survey?.client_name,
      'client_address': invoiceData?.client_address,
      'client_email': invoiceData?.client_email,
      'operator': invoiceData?.survey?.operator,
      'vessel_name': invoiceData?.survey?.vessel_name,
      'client_ref_no': invoiceData?.survey?.client_ref_no,
      'survey_country': invoiceData?.survey?.survey_country,
      'survey_port': invoiceData?.survey?.survey_port,
      'invoice_number': invoiceData?.invoice_number,
      'invoice_title': meta.custom_invoice_title || (invoiceData?.kbl_account === 'UAE' ? 'TAX INVOICE' : 'INVOICE')
    }
    return String(fieldMap[field] || '')
  }

  const getRecoveryValue = (field) => {
    // Inline invoice edits overlay the bunker form draft
    if (editedRecovery[field] !== undefined) {
      return String(editedRecovery[field] || '')
    }
    if (financialDraft != null && Object.prototype.hasOwnProperty.call(financialDraft, field)) {
      return String(financialDraft[field] ?? '')
    }
    const value = invoiceData?.survey?.[field]
    return value != null && value !== '' ? String(value) : ''
  }

  const parseOptionalDecimal = (raw) => {
    if (raw === '' || raw === undefined || raw === null) return null
    const n = parseFloat(String(raw).replace(/,/g, ''))
    return Number.isFinite(n) ? n : null
  }

  const parseOptionalFloat = (raw) => {
    if (raw === '' || raw === undefined || raw === null) return null
    const n = parseFloat(String(raw).replace(/,/g, ''))
    return Number.isFinite(n) ? n : null
  }

  const INVOICE_FLOAT_PATCH_KEYS = [
    'recovery', 'pct_qty', 'mt_price',
    'lsfo_logbook', 'lsfo_survey', 'lsfo_qty', 'lsfo_price', 'lsfo_recovery',
    'lsfo_bdn', 'lsfo_vessel',
    'hsfo_logbook', 'hsfo_survey', 'hsfo_qty', 'hsfo_price', 'hsfo_recovery',
    'hsfo_bdn', 'hsfo_vessel',
    'mgo_logbook', 'mgo_survey', 'mgo_qty', 'mgo_price', 'mgo_recovery',
    'mgo_bdn', 'mgo_vessel'
  ]

  const buildInvoiceSavePayload = () => {
    if (!invoiceData?.survey) return null
    const survey = invoiceData.survey
    const ref = survey.reference_number
    const defaultInvNum = `INV-${ref}`
    const defaultTitle = invoiceData.kbl_account === 'UAE' ? 'TAX INVOICE' : 'INVOICE'
    const numStr = getDetailValue('invoice_number').trim()
    const titleStr = getDetailValue('invoice_title').trim()
    const metaBase = invoiceData.invoice_meta || survey.invoice_meta || {}

    const fd = financialDraft
    const payload = {
      survey_fee: parseOptionalDecimal(
        fd ? fd.survey_fee : getItemValue('survey_fee', 'amount')
      ),
      expenses: parseOptionalDecimal(fd ? fd.expenses : getItemValue('travel_expenses', 'amount')),
      recovery: parseOptionalDecimal(fd ? fd.recovery : getItemValue('incentive', 'amount')),
      others: parseOptionalDecimal(
        sumOthersLineAmounts(emptyRows, { onlySelected: true }).toFixed(2)
      ),
      discount: parseOptionalDecimal(
        editedDiscount !== null && editedDiscount !== undefined
          ? editedDiscount
          : (fd?.discount ?? invoiceData.discount)
      ),
      operator: getDetailValue('operator'),
      vessel_name: getDetailValue('vessel_name'),
      client_name: getDetailValue('client_name'),
      client_ref_no: getDetailValue('client_ref_no') || null,
      survey_country: getDetailValue('survey_country'),
      survey_port: getDetailValue('survey_port'),
      client_address: getDetailValue('client_address'),
      client_email: getDetailValue('client_email'),
      invoice_meta: {
        ...metaBase,
        selectedItems,
        currency,
        extra_lines: emptyRows.map(({ id, description, amount, selected }) => ({
          id,
          description,
          amount,
          selected: selected !== false,
        })),
        custom_invoice_number: numStr === defaultInvNum ? null : numStr,
        custom_invoice_title: titleStr === defaultTitle ? null : titleStr,
        label_overrides: {
          ...(editedDetails.discount_label !== undefined && { discount_label: editedDetails.discount_label }),
          ...(editedDetails.tax_label !== undefined && { tax_label: editedDetails.tax_label }),
          ...(editedDetails.total_label !== undefined && { total_label: editedDetails.total_label })
        },
        total_override: editedTotalAmount,
        line_descriptions: {
          survey_fee: getItemValue('survey_fee', 'description'),
          travel_expenses: getItemValue('travel_expenses', 'description'),
          incentive: getItemValue('incentive', 'description'),
          others: getItemValue('others', 'description')
        }
      }
    }

    INVOICE_FLOAT_PATCH_KEYS.forEach((key) => {
      payload[key] = parseOptionalFloat(getRecoveryValue(key))
    })

    if (pctDisabled) payload.pct_qty = null
    if (mtPriceDisabled) payload.mt_price = null

    return payload
  }

  const persistInvoice = useCallback(async () => {
    if (!id || !invoiceData || !dirtyRef.current || suspendAutoSaveRef.current) return
    try {
      setSaveStatus('saving')
      setSaveError(null)
      const payload = buildInvoiceSavePayload()
      if (!payload) return
      const response = await surveyAPI.saveInvoice(id, payload)
      if (!response.data.success) {
        throw new Error(response.data.message || 'Save failed')
      }
      setSaveStatus('saved')
      dirtyRef.current = false
      await loadInvoiceData()
    } catch (err) {
      console.error('Invoice save failed:', err)
      dirtyRef.current = true
      setSaveStatus('error')
      setSaveError(err.response?.data?.message || err.message || 'Save failed')
    }
  }, [
    id,
    invoiceData,
    editedItems,
    editedDetails,
    editedRecovery,
    editedDiscount,
    editedTotalAmount,
    selectedItems,
    emptyRows,
    currency,
    financialDraft,
    pctDisabled,
    mtPriceDisabled,
    loadInvoiceData,
  ])

  useEffect(() => {
    if (!invoiceData || !dirtyRef.current) return undefined
    const timer = setTimeout(() => {
      persistInvoice()
    }, 1000)
    return () => clearTimeout(timer)
  }, [
    invoiceData,
    editedItems,
    editedDetails,
    editedRecovery,
    editedDiscount,
    editedTotalAmount,
    selectedItems,
    emptyRows,
    currency,
    financialDraft,
    pctDisabled,
    mtPriceDisabled,
    persistInvoice,
  ])

  const handlePrint = () => {
    const invoiceNumber = getDetailValue('invoice_number')
    const originalTitle = document.title
    document.title = `${invoiceNumber}.pdf`
    window.print()
    document.title = originalTitle
  }


  const handleItemToggle = (item) => {
    markDirty()
    setSelectedItems(prev => ({
      ...prev,
      [item]: !prev[item]
    }))
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-4 border-gray-900"></div>
          <p className="text-gray-600 mt-4">Loading invoice...</p>
        </div>
      </div>
    )
  }

  if (error || !invoiceData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error || 'Invoice not found'}</p>
          <button
            onClick={() => navigate('/surveys')}
            className="px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800"
          >
            Back to Surveys
          </button>
        </div>
      </div>
    )
  }

  const {
    survey,
    company_info,
    due_date,
    client_address,
    client_email,
    available_items: rawAvailableItems,
    bank_info,
    kbl_account,
  } = invoiceData

  const available_items = {
    ...(rawAvailableItems || {}),
    others: rawAvailableItems?.others ?? {
      description: 'Others',
      amount: 0,
    },
  }
  const totals = calculateTotals()
  const displayedTotalAmountDue =
    editedTotalAmount !== null ? (parseFloat(editedTotalAmount) || 0) : (totals.total || 0)
  const displayedTaxAmount = roundTo2(displayedTotalAmountDue * 0.05)

  return (
    <div className="min-h-screen bg-gray-50 py-8 invoice-page">
      <div className="edit-controls no-print flex flex-wrap items-center gap-4">
        <span className="text-sm text-gray-600">
          {saveStatus === 'saving' && 'Saving changes…'}
          {saveStatus === 'saved' && 'All changes saved'}
          {saveStatus === 'error' && (
            <span className="text-red-600">{saveError || 'Could not save'}</span>
          )}
          {saveStatus === 'idle' && (
            <span className="text-gray-500">Changes save automatically shortly after you edit.</span>
          )}
        </span>
      </div>

      <style>{`
        @media print {
          /* Hide everything except invoice content */
          body {
            margin: 0 !important;
            padding: 0 !important;
            background: white !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          
          /* Hide navigation and all non-print elements */
          header,
          nav,
          .no-print,
          .print-hidden,
          button,
          .btn,
          .fixed,
          [class*="fixed"],
          .currency-selector {
            display: none !important;
          }
          
          /* Specifically hide the Layout header */
          body > div > header,
          body > div > nav,
          .print-hidden {
            display: none !important;
            visibility: hidden !important;
          }
          
          /* Hide main padding from Layout */
          main {
            padding-top: 0 !important;
            margin-top: 0 !important;
          }
          
          /* Remove all top spacing */
          .pt-24 {
            padding-top: 0 !important;
          }
          
          /* Remove all page margins - maximize space */
          @page {
            margin: 0.3cm !important;
            size: A4;
          }
          
          /* Container adjustments */
          .invoice-page {
            margin: 0 !important;
            padding: 0 !important;
            background: white !important;
          }
          
          .container {
            max-width: 100% !important;
            width: 100% !important;
            padding: 0 !important;
            margin: 0 !important;
          }
          
          /* Card adjustments */
          .bg-white {
            border: none !important;
            box-shadow: none !important;
            margin: 0 !important;
            padding: 0 !important;
            page-break-inside: avoid !important;
          }
          
          /* Reduce all padding and margins */
          .p-6 {
            padding: 0.5rem !important;
          }
          
          .py-8 {
            padding-top: 0 !important;
            padding-bottom: 0 !important;
          }
          
          .mb-2 {
            margin-bottom: 0.15rem !important;
          }
          
          .mb-3 {
            margin-bottom: 0.25rem !important;
          }
          
          .mt-1 {
            margin-top: 0.1rem !important;
          }
          
          /* Header section - compact with spacing */
          .border-b-2 {
            border-bottom-width: 1px !important;
            padding: 0.5rem !important;
            margin-bottom: 1rem !important;
            padding-bottom: 0.8rem !important;
          }
          
          .border-b {
            border-bottom-width: 1px !important;
            padding: 0.5rem !important;
          }
          
          /* Add spacing after header in print */
          #invoice-header {
            margin-bottom: 1rem !important;
            padding-bottom: 0.8rem !important;
          }
          
          /* Add spacing before client section in print */
          .invoice-content-wrapper > div:first-child {
            margin-top: 0.8rem !important;
            padding-top: 0.8rem !important;
          }
          
          /* Invoice title and number - move slightly to the right in print */
          #invoice-header .invoice-title {
            text-align: center !important;
          }
          
          #invoice-header .text-left {
            text-align: center !important;
          }
          
          /* Center invoice number */
          #invoice-header p {
            text-align: center !important;
          }
          
          /* Center the invoice meta div in print with slight right offset */
          .invoice-meta-print {
            text-align: center !important;
            padding-left: 0 !important;
          }
          
          /* USA account - move invoice title/number more to the right in print */
          .invoice-meta-usa {
            margin-left: 4rem !important;
          }
          
          /* UAE account - keep current position in print */
          .invoice-meta-uae {
            margin-left: 1rem !important;
          }
          
          /* Add spacing between client section and invoice details table */
          .client-section + div {
            margin-top: 0.8rem !important;
            padding-top: 0.5rem !important;
          }
          
          /* Consistent spacing for service items table section - same as client section */
          .p-6.border-b.border-gray-200:has(table#service-items-table) {
            margin-top: 0.8rem !important;
            padding-top: 0.8rem !important;
            margin-bottom: 0.8rem !important;
            padding-bottom: 0.8rem !important;
          }
          
          .invoice-meta-print .invoice-title,
          .invoice-meta-print h1,
          .invoice-meta-print p {
            text-align: center !important;
          }
          
          /* Prevent invoice number from wrapping to next line */
          .invoice-meta-print p {
            white-space: nowrap !important;
            display: inline-block !important;
          }
          
          /* Keep invoice title and number on same visual line */
          .invoice-meta-print {
            display: flex !important;
            flex-direction: column !important;
            align-items: center !important;
          }
          
          /* Logo size */
          img {
            max-height: 80px !important;
            width: auto !important;
            object-fit: contain !important;
          }
          
          /* Invoice title */
          .invoice-title {
            font-size: 1.8rem !important;
            font-weight: bold;
            color: #FF8C00;
            text-align: left !important;
            margin: 0 !important;
            padding: 0 !important;
          }

          /* UAE "TAX INVOICE": slightly smaller heading in PDF for better fit */
          .invoice-meta-uae .invoice-title {
            font-size: 1.5rem !important;
          }
          
          /* Company info - smaller font */
          .text-sm {
            font-size: 0.7rem !important;
            line-height: 1.2 !important;
          }
          
          .text-lg {
            font-size: 0.85rem !important;
          }
          
          /* Table adjustments */
          table {
            width: 100% !important;
            margin-bottom: 0.5rem !important;
            page-break-inside: avoid !important;
          }
          
          /* Fix invoice details table column widths in print */
          table[style*="table-layout: fixed"] {
            table-layout: fixed !important;
          }
          
          /* Force column widths for invoice details table in print */
          table[style*="table-layout: fixed"] th:nth-child(1),
          table[style*="table-layout: fixed"] td:nth-child(1) {
            width: 25% !important;
            min-width: 25% !important;
            max-width: 25% !important;
          }
          
          table[style*="table-layout: fixed"] th:nth-child(2),
          table[style*="table-layout: fixed"] td:nth-child(2) {
            width: 25% !important;
            min-width: 25% !important;
            max-width: 25% !important;
          }
          
          table[style*="table-layout: fixed"] th:nth-child(3),
          table[style*="table-layout: fixed"] td:nth-child(3) {
            width: 30% !important;
            min-width: 30% !important;
            max-width: 30% !important;
          }
          
          table[style*="table-layout: fixed"] th:nth-child(4),
          table[style*="table-layout: fixed"] td:nth-child(4) {
            width: 20% !important;
            min-width: 20% !important;
            max-width: 20% !important;
          }
          
          /* Ensure edit inputs don't affect column widths in print */
          table[style*="table-layout: fixed"] .editable-field,
          table[style*="table-layout: fixed"] .editable-amount {
            max-width: 100% !important;
            box-sizing: border-box !important;
            overflow: hidden !important;
            text-overflow: ellipsis !important;
          }
          
          /* Prevent survey location inputs from expanding column */
          table[style*="table-layout: fixed"] td:nth-child(3) .editable-field {
            max-width: calc(100% - 10px) !important;
          }
          
          table[style*="table-layout: fixed"] td:nth-child(3) .flex {
            max-width: 100% !important;
            justify-content: center !important;
          }
          
          /* Ensure Description and Amount columns maintain 70/30 ratio in print */
          #service-items-table {
            table-layout: fixed !important;
          }
          
          #service-items-table th:first-child,
          #service-items-table td:first-child {
            width: 70% !important;
          }
          
          #service-items-table th:last-child,
          #service-items-table td:last-child {
            width: 30% !important;
          }
          
          .table-header {
            background-color: #FFE4B5 !important;
            color: #996633 !important;
            font-weight: 600 !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          
          th, td {
            padding: 0.3rem 0.5rem !important;
            font-size: 0.75rem !important;
            line-height: 1.2 !important;
          }
          
          /* Service items table - very small height for description rows */
          #service-items-table td {
            font-size: 0.8rem !important;
          }
          
          #service-items-body td {
            padding: 0.15rem !important;
            line-height: 1.2 !important;
          }
          
          #service-items-body tr {
            height: auto !important;
            min-height: auto !important;
          }
          
          #service-items-body tr.invoice-tax-note-print td:first-child {
            text-align: right !important;
          }
          
          /* Totals section - blue color #d0e0fa */
          .bg-blue-500,
          tr[style*="#d0e0fa"],
          tr[style*="#87CEEB"],
          tr[style*="#B3E5FC"],
          tr[style*="#E3F2FD"],
          tr[style*="#2196F3"],
          tr[style*="#0d6efd"] {
            background-color: #d0e0fa !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          
          .text-lg {
            font-size: 0.9rem !important;
          }
          
          /* Recovery table - smaller with dashed borders */
          .text-sm table {
            font-size: 0.7rem !important;
          }
          
          .text-sm th,
          .text-sm td {
            padding: 0.25rem !important;
            font-size: 0.7rem !important;
          }
          
          /* Ensure dashed borders in print for recovery table */
          table.text-sm th,
          table.text-sm td {
            border: 1px dashed #999 !important;
          }
          
          /* Recovery table header background - lighter orange pastel for first column only */
          .recovery-table th:first-child {
            background-color: #FFF4E6 !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          
          /* Recovery table first column cells - lighter orange pastel */
          .recovery-table tbody th:first-child {
            background-color: #FFF4E6 !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          
          /* Bank details card styling for print */
          .bg-gray-50 .bg-white {
            background-color: white !important;
            border: 1px solid #e5e7eb !important;
            border-radius: 0.5rem !important;
            padding: 0.75rem !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            page-break-inside: avoid !important;
          }
          
          /* Bank details spacing for print */
          .bg-gray-50 .space-y-3 > * + *,
          .bg-gray-50 .space-y-4 > * + * {
            margin-top: 0.5rem !important;
          }
          
          /* Bank details text sizes for print */
          .bg-gray-50 .text-xs {
            font-size: 0.65rem !important;
          }
          
          .bg-gray-50 .text-sm {
            font-size: 0.75rem !important;
          }
          
          /* Bank details grid for print */
          .bg-gray-50 .grid {
            gap: 0.75rem !important;
          }
          
          /* Bank details */
          .bg-gray-50 {
            background-color: #f9fafb !important;
            padding: 0.5rem !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          
          /* Bank details rounded cards background for print */
          .bg-gray-50 .bg-gray-50 {
            background-color: #f9fafb !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          
          /* Payment reminder - no pink background */
          .bg-red-50,
          div[style*="bg-red-50"] {
            background-color: white !important;
            padding: 0.5rem !important;
            margin-top: 0.5rem !important;
          }
          
          .text-red-700 {
            color: #b91c1c !important;
            font-size: 0.7rem !important;
            line-height: 1.3 !important;
          }
          
          .text-xs {
            font-size: 0.65rem !important;
          }
          
          /* Remove gaps between sections */
          .border-b,
          .border-b-2 {
            margin-bottom: 0 !important;
          }
          
          /* Grid adjustments */
          .grid {
            gap: 0.5rem !important;
          }
          
          /* Reduce vertical spacing in all sections */
          .gap-4 {
            gap: 0.5rem !important;
          }
          
          /* Compact spacing for client info */
          .flex.items-start.gap-4 {
            gap: 0.5rem !important;
            margin-bottom: 0.2rem !important;
          }
          
          /* Align client info labels and values to left, use same font size as table */
          .flex.items-start.gap-4 > span.font-semibold {
            font-size: 0.75rem !important;
            width: auto !important;
            min-width: 60px !important;
            text-align: left !important;
            margin-right: 0.3rem !important;
          }
          
          .flex.items-start.gap-4 > span:not(.font-semibold),
          .flex.items-start.gap-4 > input,
          .flex.items-start.gap-4 > textarea {
            font-size: 0.75rem !important;
            text-align: left !important;
          }
          
          /* Client address and email alignment */
          .flex.items-start.gap-4 .whitespace-pre-line {
            font-size: 0.75rem !important;
            text-align: left !important;
          }
          
          /* Issue Date and Due Date - same font size as table */
          .col-span-4.text-right p {
            font-size: 0.75rem !important;
          }
          
          .col-span-4.text-right p strong {
            font-size: 0.75rem !important;
          }
          
          /* Add spacing between Issue Date and Due Date in print */
          .col-span-4.text-right p:first-child {
            margin-bottom: 0.8rem !important;
          }
          
          .col-span-4.text-right p:last-child {
            margin-top: 0.5rem !important;
          }
          
          /* Prevent page breaks inside important sections */
          .border-b-2,
          .border-b,
          table,
          .bg-gray-50,
          .bg-red-50 {
            page-break-inside: avoid !important;
          }
          
          /* Ensure invoice fits on one page */
          .invoice-page {
            height: auto !important;
            min-height: auto !important;
          }
          
          /* Keep invoice footer group together - prevent page breaks */
          .invoice-footer-group {
            page-break-inside: avoid !important;
            break-inside: avoid !important;
            display: block !important;
          }
          
          /* Hide edit controls in print */
          .edit-controls,
          .edit-mode-indicator {
            display: none !important;
          }
          
          /* Remove edit borders in print */
          .editable-field,
          .editable-amount,
          .editable-description {
            border: none !important;
            background: transparent !important;
          }
          
          /* Fix Bank Details and below to bottom for print */
          .bg-white {
            display: flex !important;
            flex-direction: column !important;
            min-height: auto !important;
          }
          
          .invoice-content-wrapper {
            display: flex !important;
            flex-direction: column !important;
            min-height: calc(100vh - 2rem) !important;
            justify-content: space-between !important;
          }
          
          .invoice-footer-group {
            margin-top: auto !important;
            flex-shrink: 0 !important;
          }
          
          .invoice-footer-section {
            margin-top: 0 !important;
            page-break-inside: avoid !important;
            break-inside: avoid !important;
          }
          
          /* Ensure footer sections stay together with no gaps */
          .invoice-footer-group .invoice-footer-section {
            margin-top: 0 !important;
            margin-bottom: 0 !important;
            padding-top: 0.3rem !important;
            padding-bottom: 0.3rem !important;
          }
          
          .invoice-footer-group .invoice-footer-section:first-child {
            padding-top: 0.4rem !important;
          }
          
          .invoice-footer-group .invoice-footer-section:last-child {
            padding-bottom: 0.4rem !important;
          }
          
          /* Remove all unnecessary whitespace */
          * {
            margin-top: 0 !important;
            margin-bottom: 0 !important;
          }
          
          .p-6 {
            padding: 0.3rem !important;
          }
          
          /* Make sure tables are compact */
          table {
            border-collapse: collapse !important;
          }
          
          /* Reduce Bank Details padding in print */
          .invoice-footer-group .bg-gray-50 {
            padding: 0.3rem !important;
            padding-left: 0 !important;
          }
          
          /* Ensure Bank Details container doesn't add left padding */
          .invoice-footer-group .bg-gray-50 > * {
            margin-left: 0 !important;
          }
          
          .invoice-footer-group .bg-white {
            padding: 0 !important;
          }
          
          /* Reduce Bank Details card padding */
          .invoice-footer-group .px-5 {
            padding-left: 0 !important;
            padding-right: 0.5rem !important;
            padding-top: 0 !important;
            padding-bottom: 0 !important;
          }
          
          /* Ensure currency account cards align properly in print */
          .invoice-footer-group .bg-white.rounded-lg {
            padding-left: 0 !important;
            padding-right: 0.5rem !important;
            padding-top: 0 !important;
            padding-bottom: 0 !important;
          }
          
          /* Ensure first row aligns left in print */
          .invoice-footer-group .bg-white .flex:first-child {
            padding-left: 0 !important;
            margin-left: 0 !important;
          }
          
          /* Ensure Bank Information card first row aligns properly */
          .invoice-footer-group .bg-white.rounded-lg .space-y-0 > .flex:first-child {
            padding-left: 0 !important;
            margin-left: 0 !important;
          }
          
          /* Remove any left padding/margin from first elements in cards */
          .invoice-footer-group .bg-white .space-y-0 > *:first-child {
            margin-left: 0 !important;
            padding-left: 0 !important;
          }
          
          /* Ensure all flex rows in cards start from left edge */
          .invoice-footer-group .bg-white .flex {
            margin-left: 0 !important;
            padding-left: 0 !important;
          }
          
          /* Bank details left column labels - align perfectly to left */
          .invoice-footer-group .w-36 {
            padding-left: 0 !important;
            margin-left: 0 !important;
            text-align: left !important;
          }
          
          /* Increase width of second column in bank details to prevent wrapping */
          .invoice-footer-group .flex-1 {
            min-width: 65% !important;
            width: 65% !important;
          }
          
          /* Add space between label and grid columns in bank details */
          .invoice-footer-group .flex-1.grid.grid-cols-2 {
            margin-left: 0.5rem !important;
          }
          
          /* Prevent bank address from wrapping in print */
          .invoice-footer-group .flex-1.grid.grid-cols-2 > div {
            white-space: nowrap !important;
            overflow: visible !important;
          }
          
          /* Reduce gap in bank address grid to give more space */
          .invoice-footer-group .flex-1.grid.grid-cols-2 {
            gap: 0.75rem !important;
          }
          
          /* Make beneficiary name larger in print */
          .invoice-footer-group .flex-1.text-center.font-bold {
            font-size: 0.75rem !important;
          }
          
          /* Bank details container - remove all left padding */
          .invoice-footer-group .bg-white.rounded-lg {
            padding-left: 0 !important;
          }
          
          .invoice-footer-group .bg-white.rounded-lg > * {
            padding-left: 0 !important;
            margin-left: 0 !important;
          }
          
          .invoice-footer-group .pt-5 {
            padding-top: 0.3rem !important;
          }
          
          .invoice-footer-group .pb-3 {
            padding-bottom: 0.2rem !important;
          }
          
          /* Reduce row padding in Bank Details */
          .invoice-footer-group .py-2 {
            padding-top: 0.15rem !important;
            padding-bottom: 0.15rem !important;
          }
          
          /* Reduce spacing in Bank Details */
          .invoice-footer-group .space-y-0 > * + * {
            margin-top: 0 !important;
          }
          
          /* Reduce Bank Details title margin */
          .invoice-footer-group h6 {
            margin-bottom: 0.3rem !important;
            font-size: 0.75rem !important;
          }
          
          /* Reduce Payment Reminder padding */
          .invoice-footer-group .text-red-700 {
            font-size: 0.65rem !important;
            line-height: 1.2 !important;
            margin-bottom: 0.2rem !important;
          }
          
          /* UAE invoice - increase column width and reduce company name font size */
          .invoice-footer-group .grid.grid-cols-2 {
            gap: 1rem !important;
          }
          
          .invoice-footer-group .flex-1.text-center {
            font-size: 0.65rem !important;
            word-break: break-word !important;
          }
          
          /* Ensure invoice fits on one page - reduce all spacing */
          .invoice-page {
            height: auto !important;
            max-height: 100vh !important;
            overflow: hidden !important;
          }
          
          /* Force single page - prevent page breaks */
          .container {
            page-break-after: avoid !important;
            page-break-inside: avoid !important;
          }
          
          .bg-white.shadow-lg {
            page-break-after: avoid !important;
            page-break-inside: avoid !important;
            max-height: 100vh !important;
          }
          
          /* Reduce header padding */
          #invoice-header {
            padding: 0.4rem !important;
          }
          
          /* Reduce all section padding */
          .border-b {
            padding: 0.3rem !important;
          }
        }
        
        /* Screen styles */
        .invoice-title {
          font-size: 2.5rem;
          font-weight: bold;
          color: #FF8C00;
          text-align: center;
        }
        .table-header {
          background-color: #FFE4B5;
          color: #996633;
          font-weight: 600;
        }
        
        /* Client info and date sections - same font size as table */
        .flex.items-start.gap-4 > span.font-semibold,
        .flex.items-start.gap-4 > span:not(.font-semibold),
        .flex.items-start.gap-4 .whitespace-pre-line {
          font-size: 0.875rem;
        }
        
        .col-span-4.text-right p {
          font-size: 0.875rem;
        }
        
        .col-span-4.text-right p strong {
          font-size: 0.875rem;
        }
        
        /* Recovery table - dashed borders for screen view */
        table.text-sm th,
        table.text-sm td {
          border: 1px dashed #ccc !important;
        }
        
        /* Recovery table header background for screen - lighter orange pastel for first column only */
        .recovery-table th:first-child {
          background-color: #FFF4E6 !important;
        }
        
        /* Recovery table first column cells - lighter orange pastel */
        .recovery-table tbody th:first-child {
          background-color: #FFF4E6 !important;
        }
        
        /* UAE bank details: two banks side by side. The second bank's column is pinned to the
           card's right edge with a fixed width (in em, so it scales with the print font) so it
           lines up row to row, and the first bank's column sits on a faint gray band so the two
           banks don't run together. Rows holding these columns take their vertical padding on
           the cells instead of the row, so the band runs unbroken; the !important + extra
           specificity keep the generic print rules for this section from re-padding the rows
           (which shifted the right column a few px on some rows). */
        .invoice-footer-group .flex:has(> .uae-bank-cols) {
          padding: 0 !important;
          align-items: stretch !important;
        }

        .invoice-footer-group .flex:has(> .uae-bank-cols) > .w-36 {
          display: flex;
          align-items: center;
          padding: 0.25rem 0 !important;
        }

        .invoice-footer-group .uae-bank-cols {
          flex: 1 1 0% !important;
          min-width: 0 !important;
          width: auto !important;
          display: grid !important;
          grid-template-columns: minmax(0, 1fr) 18.5em !important;
          gap: 0 !important;
          margin: 0 0 0 0.5rem !important;
          font-size: 0.875rem;
        }

        .invoice-footer-group .uae-bank-cols > div {
          display: flex;
          align-items: center;
          padding: 0.25rem 0 !important;
          white-space: nowrap;
        }

        .invoice-footer-group .uae-bank-cols > div:first-child {
          background-color: #f9fafb;
          padding-left: 0.75em !important;
          padding-right: 0.75em !important;
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }

        .invoice-footer-group .uae-bank-cols > div:last-child {
          padding-left: 0.875em !important;
        }

        @media print {
          .invoice-footer-group .uae-bank-cols {
            font-size: 0.75rem;
          }

          /* Paper washes out light grays, so the band is a touch deeper in the PDF */
          .invoice-footer-group .uae-bank-cols > div:first-child {
            background-color: #f3f4f6 !important;
          }
        }

        /* Bank details card styling for screen */
        .bg-gray-50 .bg-white {
          background-color: white;
          border: 1px solid #e5e7eb;
          border-radius: 0.5rem;
          box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
        }
        
        /* Recovery table - increase width of LSFO/HSFO/MGO column for screen */
        table.text-sm th:first-child {
          width: 20% !important;
          min-width: 20% !important;
        }
        
        /* Description rows - very small height */
        #service-items-body td {
          padding: 0.15rem !important;
          line-height: 1.2 !important;
        }
        
        #service-items-body tr {
          height: auto !important;
          min-height: auto !important;
        }
        
        #service-items-body tr.invoice-tax-note-print td:first-child {
          text-align: right !important;
        }
        
        /* Blue background color #d0e0fa */
        .bg-blue-500,
        tr[style*="#d0e0fa"],
        tr[style*="#87CEEB"],
        tr[style*="#B3E5FC"],
        tr[style*="#E3F2FD"],
        tr[style*="#2196F3"] {
          background-color: #d0e0fa !important;
        }
        
        /* Ensure Description and Amount columns maintain 70/30 ratio on screen */
        #service-items-table {
          table-layout: fixed !important;
        }
        
        #service-items-table th:first-child,
        #service-items-table td:first-child {
          width: 70% !important;
        }
        
        #service-items-table th:last-child,
        #service-items-table td:last-child {
          width: 30% !important;
        }
        
        /* Fix Bank Details and below to bottom for screen - simpler approach */
        .invoice-page {
          position: relative !important;
          z-index: 0 !important;
        }
        
        .invoice-page .container {
          position: relative !important;
          z-index: 0 !important;
        }
        
        .invoice-page .bg-white {
          position: relative !important;
          z-index: 0 !important;
        }

        /* Hide number input spinners in invoice */
        .invoice-page input[type='number']::-webkit-outer-spin-button,
        .invoice-page input[type='number']::-webkit-inner-spin-button {
          -webkit-appearance: none;
          margin: 0;
        }
        .invoice-page input[type='number'] {
          appearance: textfield;
          -moz-appearance: textfield;
        }
        
        /* Use a wrapper div approach - wrap content sections */
        .invoice-content-wrapper {
          display: flex !important;
          flex-direction: column !important;
          min-height: calc(100vh - 12rem) !important;
          justify-content: space-between !important;
        }
        
        .invoice-footer-group {
          margin-top: auto !important;
        }
        
        .invoice-footer-section {
          margin-top: 0 !important;
        }
        
        /* Ensure footer sections stay together */
        .invoice-footer-group .invoice-footer-section ~ .invoice-footer-section {
          margin-top: 0 !important;
        }
        
        /* Edit mode styles - auto-size to content */
        .editable-field:not(textarea),
        .editable-amount:not(textarea),
        .editable-description:not(textarea) {
          border: 1px dashed transparent;
          padding: 2px 5px;
          transition: all 0.2s ease;
          min-height: 1em;
          border-radius: 3px;
          width: auto !important;
          display: inline-block !important;
          min-width: 50px;
          max-width: 100%;
          vertical-align: baseline;
          margin: 0;
          line-height: inherit;
        }
        
        /* Ensure edit inputs don't shift layout - maintain same position as text */
        td .editable-field:not(textarea),
        td .editable-amount:not(textarea),
        td .editable-description:not(textarea) {
          text-align: inherit;
        }
        
        /* Keep survey location inputs centered and together */
        .flex.gap-1.items-center.justify-center {
          justify-content: center;
          align-items: center;
        }
        
        .flex.gap-1.items-center.justify-center .editable-field {
          margin: 0;
        }
        
        /* Textarea should match original text height exactly */
        textarea.editable-field {
          width: auto !important;
          display: inline-block !important;
          border: 1px dashed transparent;
          padding: 0.25rem 0.5rem;
          transition: all 0.2s ease;
          border-radius: 3px;
          resize: none !important;
          overflow: hidden;
          line-height: 1.5rem;
          vertical-align: top;
          margin: 0;
        }
        
        /* Ensure textarea matches span height in edit mode - no layout shift */
        .flex.items-start.gap-4 textarea.editable-field {
          height: auto !important;
          min-height: 1.5rem;
          max-height: none;
        }
        
        /* Match span display properties */
        .flex.items-start.gap-4 span.whitespace-pre-line {
          display: inline-block;
          line-height: 1.5rem;
          vertical-align: top;
        }
        
        /* Ensure no layout shift when switching between span and textarea */
        .flex.items-start.gap-4 > span.whitespace-pre-line,
        .flex.items-start.gap-4 > textarea.editable-field {
          margin: 0;
          padding: 0;
          vertical-align: top;
        }
        
        /* Hide textarea resize handle in print */
        @media print {
          textarea {
            resize: none !important;
            overflow: hidden !important;
          }
          
          textarea::-webkit-resizer {
            display: none !important;
          }
        }
        
        /* Override w-full and flex-1 for editable fields (except textarea) */
        .editable-field.w-full:not(textarea),
        .editable-amount.w-full:not(textarea),
        .editable-description.w-full:not(textarea) {
          width: auto !important;
          max-width: 100%;
        }
        
        .editable-field.flex-1:not(textarea),
        .editable-amount.flex-1:not(textarea),
        .editable-description.flex-1:not(textarea) {
          width: auto !important;
          flex: none !important;
          max-width: 100%;
        }
        
        .editable-field:hover,
        .editable-amount:hover,
        .editable-description:hover {
          border-color: #FF8C00;
          background-color: #fff9f0;
          cursor: text;
        }
        
        .editable-field:focus,
        .editable-amount:focus,
        .editable-description:focus {
          border-color: #FF8C00;
          background-color: #fff;
          outline: none;
        }
        
        /* Editable title styling */
        .editable-title {
          transition: all 0.2s ease;
        }
        
        .editable-title:hover {
          background-color: #fff9f0 !important;
        }
        
        .editable-title:focus {
          background-color: #fff !important;
          outline: 2px solid #3b82f6 !important;
          outline-offset: 2px !important;
        }
        
        /* Editable content styling (for all contentEditable fields) */
        .editable-content {
          transition: all 0.2s ease;
        }
        
        .editable-content:hover {
          background-color: #fff9f0 !important;
        }
        
        .editable-content:focus {
          background-color: #fff !important;
          outline: 2px solid #3b82f6 !important;
          outline-offset: 2px !important;
        }
        
        /* Ensure client section editable fields maintain font size */
        .client-section .editable-content {
          font-size: 0.875rem !important;
        }
        
        /* Client section non-editable spans should also maintain font size */
        .client-section .flex.items-start.gap-4 > span:not(.font-semibold) {
          font-size: 0.875rem !important;
        }
        
        @media print {
          .editable-title,
          .editable-content {
            outline: none !important;
            padding: 0 !important;
          }
        }
        
        .edit-mode-indicator {
          position: fixed;
          top: 20px;
          right: 20px;
          background-color: #FF8C00;
          color: white;
          padding: 8px 16px;
          border-radius: 20px;
          font-size: 0.875rem;
          font-weight: 600;
          z-index: 1000;
          box-shadow: 0 2px 8px rgba(0,0,0,0.2);
        }
        
        .edit-controls {
          position: fixed;
          bottom: 20px;
          right: 20px;
          z-index: 1000;
          display: flex;
          gap: 10px;
        }
        
        .edit-controls button {
          padding: 10px 20px;
          border-radius: 8px;
          font-size: 0.875rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
          border: none;
          box-shadow: 0 2px 8px rgba(0,0,0,0.2);
        }
        
        .edit-controls button:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0,0,0,0.3);
        }
        
        .btn-edit {
          background-color: #FF8C00;
          color: white;
        }
        
        .btn-edit:hover {
          background-color: #e67e00;
        }
        
        .btn-reset {
          background-color: #6c757d;
          color: white;
        }
        
        .btn-reset:hover {
          background-color: #5a6268;
        }
        
        /* Non-editable fields (bank details) */
        .non-editable {
          pointer-events: none;
          user-select: none;
        }
        
        /* Neon glow effects for teal elements */
        .neon-teal-glow {
          box-shadow: 0 0 10px rgba(20, 184, 166, 0.5), 0 0 20px rgba(20, 184, 166, 0.3), 0 0 30px rgba(20, 184, 166, 0.1);
        }

        .neon-teal-glow:hover {
          box-shadow: 0 0 15px rgba(20, 184, 166, 0.7), 0 0 25px rgba(20, 184, 166, 0.5), 0 0 35px rgba(20, 184, 166, 0.3);
        }

        /* Enhanced neon for buttons */
        .bg-gradient-to-r.from-teal-500.to-teal-600:hover {
          box-shadow: 0 0 20px rgba(20, 184, 166, 0.6), 0 0 30px rgba(20, 184, 166, 0.4), 0 0 40px rgba(20, 184, 166, 0.2);
        }

        /* Enhanced neon for borders */
        .border-teal-500:hover {
          box-shadow: 0 0 15px rgba(20, 184, 166, 0.4), 0 0 25px rgba(20, 184, 166, 0.2);
        }
        
      `}</style>
      
      <div className="container mx-auto px-4 max-w-6xl">
        <div className="bg-white shadow-lg rounded-lg overflow-hidden">
          {/* Header */}
          <div className="border-b-2 border-orange-500 p-6" id="invoice-header">
            <div className="grid grid-cols-12 gap-4 items-center">
              <div className="col-span-4">
                <img src="/images/kbl_logo.png" alt="KBL Logo" className="h-20 object-contain" />
              </div>
              <div className="col-span-8">
                <div className="grid gap-4 items-center" style={{ 
                  gridTemplateColumns: kbl_account === 'USA' ? '35% 65%' : '28% 72%' 
                }}>
                  {/* Left sub-column: Invoice Title and Number */}
                  <div className={`text-center invoice-meta-print ${kbl_account === 'USA' ? 'invoice-meta-usa' : 'invoice-meta-uae'}`} style={{ 
                    marginLeft: 0, 
                    paddingLeft: 0
                  }}>
                    {<div
                        contentEditable
                        suppressContentEditableWarning
                        onBlur={(e) => {
                          const defaultTitle = kbl_account === 'UAE' ? 'TAX INVOICE' : 'INVOICE'
                          const newValue = e.target.textContent.trim() || defaultTitle
                          handleDetailEdit('invoice_title', newValue)
                        }}
                        onInput={(e) => {
                          // Auto-resize width based on content with exact font styling
                          const tempSpan = document.createElement('span')
                          tempSpan.className = 'invoice-title mb-0 text-center'
                          tempSpan.style.position = 'absolute'
                          tempSpan.style.visibility = 'hidden'
                          tempSpan.style.whiteSpace = 'nowrap'
                          tempSpan.style.fontSize = '2.5rem'
                          tempSpan.style.fontWeight = 'bold'
                          tempSpan.style.fontFamily = 'inherit'
                          tempSpan.textContent = e.target.textContent || (kbl_account === 'UAE' ? 'TAX INVOICE' : 'INVOICE')
                          document.body.appendChild(tempSpan)
                          const width = tempSpan.offsetWidth
                          document.body.removeChild(tempSpan)
                          e.target.style.width = `${Math.max(width + 20, titleWidth || 150)}px`
                        }}
                        className="invoice-title mb-0 text-center editable-title"
                        style={{ 
                          color: '#FF8C00', 
                          fontWeight: 'bold', 
                          fontSize: '2.5rem',
                          borderRadius: '4px',
                          padding: '2px 4px',
                          width: titleWidth ? `${titleWidth}px` : 'auto',
                          minWidth: titleWidth ? `${titleWidth}px` : '150px',
                          display: 'inline-block',
                          cursor: 'text'
                        }}
                        ref={titleMeasureRef}
                      >
                        {getDetailValue('invoice_title') || (kbl_account === 'UAE' ? 'TAX INVOICE' : 'INVOICE')}
                      </div>}
                    {<div
                        contentEditable
                        suppressContentEditableWarning
                        onBlur={(e) => {
                          const newValue = e.target.textContent.trim()
                          handleDetailEdit('invoice_number', newValue)
                        }}
                        onInput={(e) => {
                          const text = e.target.textContent || ''
                          const width = measureTextWidth(text, {
                            fontSize: '1.125rem',
                            fontFamily: window.getComputedStyle(e.target).fontFamily
                          })
                          e.target.style.width = `${Math.max(80, Math.min(width + 20, 200))}px`
                        }}
                        className="editable-content text-center text-gray-600 text-lg mt-1"
                        style={{
                          fontSize: '1.125rem',
                          color: '#4b5563',
                          minWidth: '80px',
                          width: 'auto',
                          whiteSpace: 'nowrap',
                          wordBreak: 'normal',
                          overflowWrap: 'normal',
                        }}
                      >
                        {getDetailValue('invoice_number')}
                      </div>}
                  </div>
                  {/* Right sub-column: Company Info */}
                  <div className="text-right">
                    <div className="font-semibold text-gray-900">{company_info.name}</div>
                    <div className="text-sm text-gray-600 whitespace-pre-line">{company_info.address}</div>
                    <div className="text-sm text-gray-600">{company_info.tel}</div>
                    <div className="text-sm text-gray-600">{company_info.email}</div>
                    {kbl_account === 'UAE' && (
                      <div className="text-sm text-gray-600">VAT-104504700600003</div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Content Wrapper */}
          <div className="invoice-content-wrapper">
          {/* Spacer for additional space between header and client section */}
          <div style={{ height: '2rem' }} className="no-print"></div>
          
          {/* Client and Date Info */}
          <div className="p-6 border-b border-gray-200 client-section" style={{ marginTop: '0.8rem', paddingTop: '0.8rem', marginBottom: '0.8rem', paddingBottom: '0.8rem' }}>
            <div className="grid grid-cols-12 gap-4">
              <div className="col-span-8">
                <div className="flex items-start gap-4 mb-2">
                  <span className="font-semibold w-24">Issued For:</span>
                  {<div
                      contentEditable
                      suppressContentEditableWarning
                      onBlur={(e) => {
                        const newValue = e.target.textContent.trim()
                        handleDetailEdit('client_name', newValue)
                      }}
                      onInput={(e) => {
                        const text = e.target.textContent || ''
                        const width = measureTextWidth(text, {
                          fontFamily: window.getComputedStyle(e.target).fontFamily
                        })
                        e.target.style.width = `${Math.max(100, Math.min(width + 20, 400))}px`
                      }}
                      className="editable-content"
                      style={{
                        fontSize: '0.875rem',
                        minWidth: '100px',
                        width: 'auto'
                      }}
                    >
                      {getDetailValue('client_name')}
                    </div>}
                </div>
                {client_address && (
                  <div className="flex items-start gap-4 mb-2">
                    <span className="font-semibold w-24" style={{ visibility: 'hidden' }}>Address:</span>
                    {<div
                        contentEditable
                        suppressContentEditableWarning
                        onBlur={(e) => {
                          const newValue = e.target.textContent.trim()
                          handleDetailEdit('client_address', newValue)
                        }}
                        onInput={(e) => {
                          // Auto-resize height based on content
                          e.target.style.height = 'auto'
                          e.target.style.height = `${Math.max(e.target.scrollHeight, 24)}px`
                          // Auto-resize width based on content
                          const text = e.target.textContent || ''
                          const width = measureTextWidth(text, {
                            fontFamily: window.getComputedStyle(e.target).fontFamily,
                            fontSize: '0.875rem',
                            whiteSpace: 'pre-line'
                          })
                          e.target.style.width = `${Math.max(200, Math.min(width + 20, 400))}px`
                        }}
                        className="editable-content whitespace-pre-line"
                        style={{
                          fontSize: '0.875rem',
                          lineHeight: '24px',
                          minWidth: '200px',
                          width: 'auto',
                          minHeight: '24px',
                          display: 'inline-block',
                          verticalAlign: 'top',
                          margin: 0,
                          padding: '2px 4px',
                          boxSizing: 'border-box'
                        }}
                        ref={addressSpanRef}
                      >
                        {getDetailValue('client_address')}
                      </div>}
                  </div>
                )}
                {survey.operator && (
                  <div className="flex items-start gap-4 mb-2">
                    <span className="font-semibold w-24">Attn:</span>
                  {<div
                      contentEditable
                      suppressContentEditableWarning
                      onBlur={(e) => {
                        const newValue = e.target.textContent.trim()
                        handleDetailEdit('operator', newValue)
                      }}
                      onInput={(e) => {
                        const text = e.target.textContent || ''
                        const width = measureTextWidth(text, {
                          fontFamily: window.getComputedStyle(e.target).fontFamily
                        })
                        e.target.style.width = `${Math.max(100, Math.min(width + 20, 300))}px`
                      }}
                      className="editable-content"
                      style={{
                        fontSize: '0.875rem',
                        minWidth: '100px',
                        width: 'auto'
                      }}
                    >
                      {getDetailValue('operator')}
                    </div>}
                  </div>
                )}
                {client_email && (
                  <div className="flex items-start gap-4">
                    <span className="font-semibold w-24" style={{ visibility: 'hidden' }}>Email:</span>
                    {<div
                        contentEditable
                        suppressContentEditableWarning
                        onBlur={(e) => {
                          const newValue = e.target.textContent.trim()
                          handleDetailEdit('client_email', newValue)
                        }}
                        onInput={(e) => {
                          const text = e.target.textContent || ''
                          const width = measureTextWidth(text, {
                            fontFamily: window.getComputedStyle(e.target).fontFamily
                          })
                          e.target.style.width = `${Math.max(150, Math.min(width + 20, 350))}px`
                        }}
                      className="editable-content"
                      style={{
                        fontSize: '0.875rem',
                        minWidth: '150px',
                        width: 'auto'
                      }}
                    >
                      {getDetailValue('client_email')}
                    </div>}
                  </div>
                )}
              </div>
              <div className="col-span-4 text-right">
                <p className="mb-3"><strong>Issue Date:</strong> {formatDate(survey.attendance_date)}</p>
                <p className="text-red-600 mt-2"><strong>Due Date:</strong> {formatDate(due_date)}</p>
              </div>
            </div>
          </div>

          {/* Invoice Details Table */}
          <div className="p-6 border-b border-gray-200" style={{ marginTop: '0.8rem', paddingTop: '0.5rem' }}>
            <table className="w-full border border-gray-300" style={{ tableLayout: 'fixed' }}>
              <thead>
                <tr className="table-header">
                  <th className="p-2 text-center border border-gray-300" style={{ width: '25%' }}>Invoice #</th>
                  <th className="p-2 text-center border border-gray-300" style={{ width: '25%' }}>Vessel Name</th>
                  <th className="p-2 text-center border border-gray-300" style={{ width: '30%' }}>Survey Location</th>
                  <th className="p-2 text-center border border-gray-300" style={{ width: '20%' }}>Survey Date</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="p-2 text-center border border-gray-300">
                    {<div
                        contentEditable
                        suppressContentEditableWarning
                        onBlur={(e) => {
                          const newValue = e.target.textContent.trim()
                          handleDetailEdit('invoice_number', newValue)
                        }}
                        onInput={(e) => {
                          const text = e.target.textContent || ''
                          const width = measureTextWidth(text, {
                            fontFamily: window.getComputedStyle(e.target).fontFamily
                          })
                          e.target.style.width = `${Math.max(100, Math.min(width + 20, 200))}px`
                        }}
                        className="editable-content text-center"
                        style={{
                          minWidth: '100px',
                          width: 'auto',
                          margin: '0 auto',
                          display: 'inline-block',
                          whiteSpace: 'nowrap',
                          wordBreak: 'normal',
                          overflowWrap: 'normal',
                        }}
                      >
                        {getDetailValue('invoice_number')}
                      </div>}
                  </td>
                  <td className="p-2 text-center border border-gray-300">
                    {<div className="flex flex-col items-center">
                        <div
                          contentEditable
                          suppressContentEditableWarning
                          onBlur={(e) => {
                            const newValue = e.target.textContent.trim()
                            handleDetailEdit('vessel_name', newValue)
                          }}
                          onInput={(e) => {
                            const text = e.target.textContent || ''
                            const width = measureTextWidth(text, {
                              fontFamily: window.getComputedStyle(e.target).fontFamily
                            })
                            e.target.style.width = `${Math.max(100, Math.min(width + 20, 300))}px`
                          }}
                          className="editable-content text-center mb-1"
                          style={{
                            minWidth: '100px',
                            width: 'auto',
                            margin: '0 auto',
                            display: 'inline-block'
                          }}
                        >
                          {getDetailValue('vessel_name')}
                        </div>
                        {survey.client_ref_no && (
                          <small className="text-gray-500">
                            (<div
                              contentEditable
                              suppressContentEditableWarning
                              onBlur={(e) => {
                                const newValue = e.target.textContent.trim()
                                handleDetailEdit('client_ref_no', newValue)
                              }}
                              onInput={(e) => {
                                const text = e.target.textContent || ''
                                const width = measureTextWidth(text, {
                                  fontSize: '0.75rem',
                                  fontFamily: window.getComputedStyle(e.target).fontFamily
                                })
                                e.target.style.width = `${Math.max(60, Math.min(width + 10, 120))}px`
                              }}
                              className="editable-content text-center text-xs"
                              style={{
                                fontSize: '0.75rem',
                                minWidth: '60px',
                                width: 'auto',
                                display: 'inline-block'
                              }}
                            >
                              {getDetailValue('client_ref_no')}
                            </div>)
                          </small>
                        )}
                      </div>}
                  </td>
                  <td className="p-2 text-center border border-gray-300">
                    {<div className="flex gap-1 items-center justify-center">
                        <div
                          contentEditable
                          suppressContentEditableWarning
                          onBlur={(e) => {
                            const newValue = e.target.textContent.trim()
                            handleDetailEdit('survey_country', newValue)
                          }}
                          onInput={(e) => {
                            const text = e.target.textContent || ''
                            const width = measureTextWidth(text, {
                              fontFamily: window.getComputedStyle(e.target).fontFamily
                            })
                            e.target.style.width = `${Math.max(60, Math.min(width + 20, 100))}px`
                          }}
                          className="editable-content text-center"
                          style={{
                            minWidth: '60px',
                            width: 'auto',
                            display: 'inline-block'
                          }}
                        >
                          {getDetailValue('survey_country')}
                        </div>
                        <span>/</span>
                        <div
                          contentEditable
                          suppressContentEditableWarning
                          onBlur={(e) => {
                            const newValue = e.target.textContent.trim()
                            handleDetailEdit('survey_port', newValue)
                          }}
                          onInput={(e) => {
                            const text = e.target.textContent || ''
                            const width = measureTextWidth(text, {
                              fontFamily: window.getComputedStyle(e.target).fontFamily
                            })
                            e.target.style.width = `${Math.max(60, Math.min(width + 20, 100))}px`
                          }}
                          className="editable-content text-center"
                          style={{
                            minWidth: '60px',
                            width: 'auto',
                            display: 'inline-block'
                          }}
                        >
                          {getDetailValue('survey_port')}
                        </div>
                      </div>}
                  </td>
                  <td className="p-2 text-center border border-gray-300">{formatDate(survey.attendance_date)}</td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Invoice workspace: amounts + descriptions (screen only) — aligns with printable lines below */}
          <div className="p-6 bg-gray-50 border-b border-gray-200 no-print">
            <div className="flex flex-wrap items-start justify-between gap-4 mb-5">
              <div>
                <h6 className="text-blue-700 font-semibold text-base">Invoice lines &amp; selections</h6>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
              {CORE_CHARGE_LINE_KEYS.filter((itemKey) => available_items[itemKey]).map((itemKey) => {
                const checked = selectedItems[itemKey]
                const labelText =
                  available_items[itemKey]?.description?.replace(/^•\s*/, '') ||
                  itemKey.replace(/_/g, ' ')
                return (
                  <div
                    key={itemKey}
                    className="grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-3 items-center rounded-lg border border-gray-200 bg-white px-3 py-2.5 shadow-sm"
                  >
                    <label className="md:col-span-3 flex items-center gap-2 cursor-pointer select-none font-medium text-gray-800">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => handleItemToggle(itemKey)}
                        className="w-4 h-4 rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                      />
                      <span className="text-sm capitalize">{labelText}</span>
                    </label>
                    <input
                      type="text"
                      aria-label={`${itemKey} description`}
                      disabled={!checked}
                      className="md:col-span-6 rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-orange-500 focus:ring-2 focus:ring-orange-300/40 disabled:bg-gray-100 disabled:text-gray-400"
                      value={getItemValue(itemKey, 'description')}
                      onChange={(e) => handleItemEdit(itemKey, 'description', e.target.value)}
                      title={
                        itemKey === 'survey_fee'
                          ? 'Survey type is added automatically when the invoice is first opened; your edits are saved on this invoice.'
                          : undefined
                      }
                    />
                    <input
                      type="number"
                      step="0.01"
                      disabled={!checked}
                      aria-label={`${itemKey} amount`}
                      className="md:col-span-3 rounded-md border border-gray-300 px-3 py-2 text-sm text-right tabular-nums focus:border-orange-500 focus:ring-2 focus:ring-orange-300/40 disabled:bg-gray-100 disabled:text-gray-400"
                      value={getItemValue(itemKey, 'amount')}
                      onChange={(e) => handleItemEdit(itemKey, 'amount', e.target.value)}
                    />
                  </div>
                )
              })}

              <div className="grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-3 items-center rounded-lg border border-gray-200 bg-white px-3 py-2.5 shadow-sm">
                <label className="md:col-span-3 flex items-center gap-2 cursor-pointer select-none font-medium text-gray-800">
                  <input
                    type="checkbox"
                    checked={selectedItems.discount}
                    onChange={() => handleItemToggle('discount')}
                    className="w-4 h-4 rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                  />
                  <span className="text-sm">Discount</span>
                </label>
                <input
                  type="text"
                  aria-label="Discount description"
                  disabled={!selectedItems.discount}
                  className="md:col-span-6 rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-orange-500 focus:ring-2 focus:ring-orange-300/40 disabled:bg-gray-100 disabled:text-gray-400"
                  value={
                    editedDetails.discount_label !== undefined
                      ? editedDetails.discount_label
                      : 'Discount'
                  }
                  onChange={(e) => handleDetailEdit('discount_label', e.target.value)}
                />
                <input
                  type="number"
                  step="0.01"
                  disabled={!selectedItems.discount}
                  aria-label="Discount amount"
                  className="md:col-span-3 rounded-md border border-gray-300 px-3 py-2 text-sm text-right tabular-nums focus:border-orange-500 focus:ring-2 focus:ring-orange-300/40 disabled:bg-gray-100 disabled:text-gray-400"
                  value={
                    editedDiscount !== null && editedDiscount !== undefined
                      ? String(editedDiscount)
                      : String(financialDraft?.discount ?? invoiceData.discount ?? '')
                  }
                  onChange={(e) => handleDiscountWorkspaceChange(e.target.value)}
                />
              </div>

              {available_items.others ? (
                <div className="rounded-lg border border-gray-200 bg-white px-3 py-2.5 shadow-sm space-y-3 md:col-span-2">
                  <div className="flex flex-wrap items-center gap-2 md:gap-3">
                    <label className="flex items-center gap-2 cursor-pointer select-none font-medium text-gray-800 shrink-0">
                      <input
                        type="checkbox"
                        checked={selectedItems.others}
                        onChange={() => handleItemToggle('others')}
                        className="w-4 h-4 rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                      />
                      <span className="text-sm capitalize">
                        {available_items.others?.description?.replace(/^•\s*/, '') || 'Others'}
                      </span>
                    </label>
                    <input
                      type="text"
                      aria-label="others description"
                      disabled
                      readOnly
                      className="flex-1 min-w-[220px] rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-orange-500 focus:ring-2 focus:ring-orange-300/40 disabled:bg-gray-100 disabled:text-gray-400"
                      value={getItemValue('others', 'description')}
                    />
                    <input
                      type="number"
                      step="0.01"
                      disabled={!selectedItems.others}
                      title={
                        selectedItems.others
                          ? 'Others is always the sum of breakdown lines below (saved as Survey.others)'
                          : undefined
                      }
                      aria-label="others total amount"
                      className="w-40 rounded-md border border-gray-300 px-3 py-2 text-sm text-right tabular-nums focus:border-orange-500 focus:ring-2 focus:ring-orange-300/40 disabled:bg-gray-100 disabled:text-gray-400"
                      value={
                        selectedItems.others
                          ? sumOthersLineAmounts(emptyRows, { onlySelected: true }).toFixed(2)
                          : getItemValue('others', 'amount')
                      }
                      onChange={(e) => handleItemEdit('others', 'amount', e.target.value)}
                    />
                  </div>
                  {selectedItems.others ? (
                    <div className="space-y-2">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 md:gap-3">
                        {emptyRows.map((row) => (
                          <div
                            key={row.id}
                            className="flex items-center gap-2 rounded-md border border-gray-200 bg-gray-50 px-2 py-2"
                          >
                            <label className="flex shrink-0 items-center cursor-pointer select-none">
                              <input
                                type="checkbox"
                                checked={row.selected !== false}
                                onChange={() => handleEmptyRowToggle(row.id)}
                                className="w-4 h-4 rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                                aria-label="Include this Others line on the invoice"
                              />
                            </label>
                            <input
                              type="text"
                              aria-label="Others breakdown description"
                              disabled={row.selected === false}
                              className="flex-1 min-w-0 rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-orange-500 focus:ring-2 focus:ring-orange-300/40 disabled:bg-gray-100 disabled:text-gray-400"
                              placeholder="Description"
                              value={row.description}
                              onChange={(e) => handleEmptyRowEdit(row.id, 'description', e.target.value)}
                            />
                            <input
                              type="number"
                              step="0.01"
                              aria-label="Others breakdown amount"
                              disabled={row.selected === false}
                              className="w-28 rounded-md border border-gray-300 px-3 py-2 text-sm text-right tabular-nums focus:border-orange-500 focus:ring-2 focus:ring-orange-300/40 disabled:bg-gray-100 disabled:text-gray-400"
                              value={row.amount}
                              onChange={(e) => handleEmptyRowEdit(row.id, 'amount', e.target.value)}
                            />
                            <button
                              type="button"
                              aria-label="Remove breakdown line"
                              title="Remove this line"
                              className="shrink-0 min-w-[2.75rem] px-3 py-2 rounded-md border border-red-200 bg-red-50 text-sm font-semibold text-red-700 hover:bg-red-100"
                              onClick={() => removeOthersBreakdownLine(row.id)}
                            >
                              X
                            </button>
                          </div>
                        ))}
                      </div>
                      <div className="flex justify-end">
                        <button
                          type="button"
                          onClick={addOthersBreakdownLine}
                          aria-label="Add breakdown line"
                          className={`inline-flex min-w-[5rem] items-center justify-center px-3 py-2 rounded-md text-sm font-semibold shadow-sm transition-colors ${
                            selectedItems.others
                              ? 'bg-gradient-to-r from-teal-500 to-teal-600 text-white hover:from-teal-600 hover:to-teal-700'
                              : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                          }`}
                          disabled={!selectedItems.others}
                        >
                          Add
                        </button>
                      </div>
                    </div>
                  ) : null}
                </div>
              ) : null}

              <div className="grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-3 items-center rounded-lg border border-gray-200 bg-white px-3 py-2.5 shadow-sm md:col-span-2">
                <div className="md:col-span-3 flex items-center gap-2 select-none font-medium text-gray-800">
                  <input
                    type="checkbox"
                    checked
                    disabled
                    className="w-4 h-4 rounded border-gray-300 text-orange-600 opacity-70 cursor-not-allowed"
                    aria-label="Total is always printed on this invoice"
                  />
                  <span className="text-sm">Total Amount Due</span>
                </div>
                <input
                  type="text"
                  aria-label="Total label"
                  className="md:col-span-6 rounded-md border border-gray-300 px-3 py-2 text-sm disabled:bg-gray-100 disabled:text-gray-700"
                  disabled
                  readOnly
                  value={
                    editedDetails.total_label !== undefined
                      ? editedDetails.total_label
                      : 'Total Amount Due'
                  }
                />
                <input
                  type="text"
                  inputMode="decimal"
                  aria-label="Total amount"
                  placeholder={formatChargeAlways(roundTo2(totals.total))}
                  className="md:col-span-3 rounded-md border border-gray-300 px-3 py-2 text-sm text-right tabular-nums disabled:bg-gray-100 disabled:text-gray-700"
                  disabled
                  readOnly
                  value={
                    editedTotalAmount !== null
                      ? formatChargeAlways(parseFloat(editedTotalAmount) || 0)
                      : formatChargeAlways(roundTo2(totals.total))
                  }
                />
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-x-8 gap-y-3 pt-4 border-t border-gray-200">
              <label className="flex items-center gap-2 cursor-pointer text-sm font-medium text-gray-800">
                <input
                  type="checkbox"
                  checked={selectedItems.tax}
                  onChange={() => handleItemToggle('tax')}
                  className="w-4 h-4 rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                />
                Show tax note (5%)
              </label>
              <label className="flex items-center gap-2 cursor-pointer text-sm font-medium text-gray-800">
                <input
                  type="checkbox"
                  checked={selectedItems.recovery}
                  onChange={() => handleItemToggle('recovery')}
                  className="w-4 h-4 rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                />
                Recovery table on invoice
              </label>
            </div>
          </div>

          {/* Service Items Table */}
          <div className="p-6 border-b border-gray-200" style={{ marginTop: '0.8rem', paddingTop: '0.8rem', marginBottom: '0.8rem', paddingBottom: '0.8rem' }}>
            <table id="service-items-table" className="w-full border border-gray-300">
              <thead>
                <tr className="table-header">
                  <th className="p-3 text-left border border-gray-300" style={{ width: '70%' }}>Description</th>
                  <th className="p-3 text-right border border-gray-300" style={{ width: '30%' }}>
                    <div className="flex items-center justify-end gap-2">
                      <select
                        value={currency}
                        onChange={(e) => {
                          markDirty()
                          setCurrency(e.target.value)
                        }}
                        className="currency-selector px-2 py-1 text-sm border border-gray-400 rounded bg-gray-100 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:bg-gray-200 font-medium text-gray-700"
                        style={{ minWidth: '60px' }}
                      >
                        <option value="USD">$ USD</option>
                        <option value="EUR">€ EUR</option>
                      </select>
                      <span>Amount</span>
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody id="service-items-body">
                {CORE_CHARGE_LINE_KEYS.map((itemKey) => {
                  if (!available_items[itemKey]) return null
                  if (!selectedItems[itemKey]) return null
                  const desc = getItemValue(itemKey, 'description')
                  const amt = parseFloat(getItemValue(itemKey, 'amount')) || 0
                  return (
                    <tr key={itemKey}>
                      <td className="p-1 border border-gray-300" style={{ padding: '0.15rem' }}>
                        {desc}
                      </td>
                      <td className="p-1 text-right border border-gray-300 tabular-nums" style={{ padding: '0.15rem' }}>
                        {formatCurrency(amt)}
                      </td>
                    </tr>
                  )
                })}
                {selectedItems.others && available_items.others ? (
                  <>
                    {emptyRows.length === 0 ? (
                      <tr key="others-total">
                        <td className="p-1 border border-gray-300" style={{ padding: '0.15rem' }}>
                          <strong>{getItemValue('others', 'description')}</strong>
                        </td>
                        <td className="p-1 text-right border border-gray-300 tabular-nums" style={{ padding: '0.15rem' }}>
                          {formatCurrency(
                            sumOthersLineAmounts(emptyRows, { onlySelected: true })
                          )}
                        </td>
                      </tr>
                    ) : null}
                    {emptyRows
                      .filter((row) => row.selected !== false)
                      .map((row) => (
                      <tr key={row.id}>
                        <td
                          className="p-1 border border-gray-300 text-gray-700 align-middle"
                          style={{ padding: '0.15rem' }}
                        >
                          {row.description}
                        </td>
                        <td className="p-1 text-right border border-gray-300 tabular-nums align-middle" style={{ padding: '0.15rem' }}>
                          {formatCurrency(parseFloat(row.amount) || 0)}
                        </td>
                      </tr>
                    ))}
                  </>
                ) : null}
                {totals.subtotal === 0 && emptyRows.length === 0 && (
                  <tr>
                    <td colSpan="2" className="p-1 text-center text-gray-500 border border-gray-300" style={{ padding: '0.15rem' }}>No charge lines</td>
                  </tr>
                )}
                {selectedItems.discount && (
                  <tr>
                    <td className="p-1 border border-gray-300" style={{ padding: '0.15rem' }}>
                      {editedDetails['discount_label'] !== undefined ? editedDetails['discount_label'] : 'Discount'}
                    </td>
                    <td className="p-1 text-right border border-gray-300 text-red-600 tabular-nums" style={{ padding: '0.15rem' }}>
                      -{formatChargeAlways(totals.discount)}
                    </td>
                  </tr>
                )}
                <tr>
                  <td
                    className="p-1 border border-blue-200 font-bold text-right"
                    style={{ padding: '0.15rem', backgroundColor: '#eff6ff' }}
                  >
                    Total Amount Due:
                  </td>
                  <td
                    className="p-1 text-right border border-blue-200 font-bold tabular-nums"
                    style={{ padding: '0.15rem', backgroundColor: '#eff6ff' }}
                  >
                    {editedTotalAmount !== null
                      ? formatChargeAlways(parseFloat(editedTotalAmount) || 0)
                      : formatChargeAlways(roundTo2(totals.total))}
                  </td>
                </tr>
                {selectedItems.tax && displayedTaxAmount > 0 && (
                  <tr className="invoice-tax-note-print" style={{ fontSize: '0.9em' }}>
                    <td className="p-1 border border-gray-300 text-right" style={{ fontStyle: 'italic', color: '#6c757d', padding: '0.15rem' }}>
                      {editedDetails['tax_label'] !== undefined ? editedDetails['tax_label'] : 'Total amount includes 5% tax'}
                    </td>
                    <td className="p-1 text-right border border-gray-300" style={{ fontStyle: 'italic', color: '#6c757d', padding: '0.15rem' }}>
                      {<div
                          contentEditable={false}
                          className="editable-content text-right w-full"
                          style={{
                            fontStyle: 'italic',
                            color: '#6c757d',
                            minWidth: '60px',
                            width: 'auto',
                            display: 'inline-block',
                            marginLeft: 'auto',
                            cursor: 'default'
                          }}
                        >
                          {formatCurrency(displayedTaxAmount)}
                        </div>}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Recovery Table */}
          {selectedItems.recovery && (
            <div className="p-6 border-b border-gray-200 recovery-table-container">
              <table className="w-full text-sm recovery-table" style={{ borderCollapse: 'collapse' }}>
                <thead>
                  <tr className="table-header">
                    <th className="p-2" style={{ border: '1px dashed #ccc', width: '20%', backgroundColor: '#FFF4E6' }}></th>
                    <th className="p-2" style={{ border: '1px dashed #ccc' }}>Logbook</th>
                    <th className="p-2" style={{ border: '1px dashed #ccc' }}>Survey</th>
                    <th className="p-2" style={{ border: '1px dashed #ccc' }}>Difference</th>
                    <th className="p-2" style={{ border: '1px dashed #ccc' }}>Price ($)</th>
                    <th className="p-2" style={{ border: '1px dashed #ccc' }}>Recovery from PCT ($)</th>
                    <th className="p-2" style={{ border: '1px dashed #ccc' }}>Recovery from MT ($)</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <th className="p-2 text-left" style={{ border: '1px dashed #ccc', width: '20%', backgroundColor: '#FFF4E6' }}>LSFO</th>
                    <td className="p-2 text-center" style={{ border: '1px dashed #ccc' }}>
                      {<div
                          contentEditable
                          suppressContentEditableWarning
                          onBlur={(e) => {
                            const newValue = e.target.textContent.trim()
                            handleRecoveryEdit('lsfo_logbook', newValue)
                          }}
                          onInput={(e) => {
                            const text = e.target.textContent || ''
                            const width = measureTextWidth(text, {
                              fontFamily: window.getComputedStyle(e.target).fontFamily
                            })
                            e.target.style.width = `${Math.max(60, width + 20)}px`
                          }}
                          className="editable-content text-center w-full"
                          style={{
                            minWidth: '60px',
                            width: 'auto',
                            display: 'inline-block',
                            margin: '0 auto'
                          }}
                        >
                          {formatFloat(parseFloat(getRecoveryValue('lsfo_logbook')) || 0)}
                        </div>}
                    </td>
                    <td className="p-2 text-center" style={{ border: '1px dashed #ccc' }}>
                      {<div
                          contentEditable
                          suppressContentEditableWarning
                          onBlur={(e) => {
                            const newValue = e.target.textContent.trim()
                            handleRecoveryEdit('lsfo_survey', newValue)
                          }}
                          onInput={(e) => {
                            const text = e.target.textContent || ''
                            const width = measureTextWidth(text, {
                              fontFamily: window.getComputedStyle(e.target).fontFamily
                            })
                            e.target.style.width = `${Math.max(60, width + 20)}px`
                          }}
                          className="editable-content text-center w-full"
                          style={{
                            minWidth: '60px',
                            width: 'auto',
                            display: 'inline-block',
                            margin: '0 auto'
                          }}
                        >
                          {formatFloat(parseFloat(getRecoveryValue('lsfo_survey')) || 0)}
                        </div>}
                    </td>
                    <td className="p-2 text-center" style={{ border: '1px dashed #ccc' }}>
                      {<div
                          contentEditable
                          suppressContentEditableWarning
                          onBlur={(e) => {
                            const newValue = e.target.textContent.trim()
                            handleRecoveryEdit('lsfo_qty', newValue)
                          }}
                          onInput={(e) => {
                            const text = e.target.textContent || ''
                            const width = measureTextWidth(text, {
                              fontFamily: window.getComputedStyle(e.target).fontFamily
                            })
                            e.target.style.width = `${Math.max(60, width + 20)}px`
                          }}
                          className="editable-content text-center w-full"
                          style={{
                            minWidth: '60px',
                            width: 'auto',
                            display: 'inline-block',
                            margin: '0 auto'
                          }}
                        >
                          {formatFloat(parseFloat(getRecoveryValue('lsfo_qty')) || 0)}
                        </div>}
                    </td>
                    <td className="p-2 text-center" style={{ border: '1px dashed #ccc' }}>
                      {<div
                          contentEditable
                          suppressContentEditableWarning
                          onBlur={(e) => {
                            const newValue = e.target.textContent.trim()
                            handleRecoveryEdit('lsfo_price', newValue)
                          }}
                          onInput={(e) => {
                            const text = e.target.textContent || ''
                            const width = measureTextWidth(text, {
                              fontFamily: window.getComputedStyle(e.target).fontFamily
                            })
                            e.target.style.width = `${Math.max(60, width + 20)}px`
                          }}
                          className="editable-content text-center w-full"
                          style={{
                            minWidth: '60px',
                            width: 'auto',
                            display: 'inline-block',
                            margin: '0 auto'
                          }}
                        >
                          {formatCurrency(parseFloat(getRecoveryValue('lsfo_price')) || 0)}
                        </div>}
                    </td>
                    <td className="p-2 text-center non-editable" style={{ border: '1px dashed #ccc' }}>
                      {(() => {
                        const recovery = parseFloat(getRecoveryValue('lsfo_recovery')) || 0
                        const pct_qty = parseFloat(getRecoveryValue('pct_qty')) || 0
                        const value = recovery && pct_qty ? recovery * pct_qty * 0.01 : 0
                        return value > 0 ? formatCurrency(value) : ''
                      })()}
                    </td>
                    <td className="p-2 text-center non-editable" style={{ border: '1px dashed #ccc' }}>
                      {(() => {
                        const qty = parseFloat(getRecoveryValue('lsfo_qty')) || 0
                        const mt_price = parseFloat(getRecoveryValue('mt_price')) || 0
                        const value = qty && mt_price ? qty * mt_price : 0
                        return value > 0 ? formatCurrency(value) : ''
                      })()}
                    </td>
                  </tr>
                  <tr>
                    <th className="p-2 text-left" style={{ border: '1px dashed #ccc', width: '20%', backgroundColor: '#FFF4E6' }}>HSFO</th>
                    <td className="p-2 text-center" style={{ border: '1px dashed #ccc' }}>
                      {<div
                          contentEditable
                          suppressContentEditableWarning
                          onBlur={(e) => {
                            const newValue = e.target.textContent.trim()
                            handleRecoveryEdit('hsfo_logbook', newValue)
                          }}
                          onInput={(e) => {
                            const text = e.target.textContent || ''
                            const width = measureTextWidth(text, {
                              fontFamily: window.getComputedStyle(e.target).fontFamily
                            })
                            e.target.style.width = `${Math.max(60, width + 20)}px`
                          }}
                          className="editable-content text-center w-full"
                          style={{
                            minWidth: '60px',
                            width: 'auto',
                            display: 'inline-block',
                            margin: '0 auto'
                          }}
                        >
                          {formatFloat(parseFloat(getRecoveryValue('hsfo_logbook')) || 0)}
                        </div>}
                    </td>
                    <td className="p-2 text-center" style={{ border: '1px dashed #ccc' }}>
                      {<div
                          contentEditable
                          suppressContentEditableWarning
                          onBlur={(e) => {
                            const newValue = e.target.textContent.trim()
                            handleRecoveryEdit('hsfo_survey', newValue)
                          }}
                          onInput={(e) => {
                            const text = e.target.textContent || ''
                            const width = measureTextWidth(text, {
                              fontFamily: window.getComputedStyle(e.target).fontFamily
                            })
                            e.target.style.width = `${Math.max(60, width + 20)}px`
                          }}
                          className="editable-content text-center w-full"
                          style={{
                            minWidth: '60px',
                            width: 'auto',
                            display: 'inline-block',
                            margin: '0 auto'
                          }}
                        >
                          {formatFloat(parseFloat(getRecoveryValue('hsfo_survey')) || 0)}
                        </div>}
                    </td>
                    <td className="p-2 text-center" style={{ border: '1px dashed #ccc' }}>
                      {<div
                          contentEditable
                          suppressContentEditableWarning
                          onBlur={(e) => {
                            const newValue = e.target.textContent.trim()
                            handleRecoveryEdit('hsfo_qty', newValue)
                          }}
                          onInput={(e) => {
                            const text = e.target.textContent || ''
                            const width = measureTextWidth(text, {
                              fontFamily: window.getComputedStyle(e.target).fontFamily
                            })
                            e.target.style.width = `${Math.max(60, width + 20)}px`
                          }}
                          className="editable-content text-center w-full"
                          style={{
                            minWidth: '60px',
                            width: 'auto',
                            display: 'inline-block',
                            margin: '0 auto'
                          }}
                        >
                          {formatFloat(parseFloat(getRecoveryValue('hsfo_qty')) || 0)}
                        </div>}
                    </td>
                    <td className="p-2 text-center" style={{ border: '1px dashed #ccc' }}>
                      {<div
                          contentEditable
                          suppressContentEditableWarning
                          onBlur={(e) => {
                            const newValue = e.target.textContent.trim()
                            handleRecoveryEdit('hsfo_price', newValue)
                          }}
                          onInput={(e) => {
                            const text = e.target.textContent || ''
                            const width = measureTextWidth(text, {
                              fontFamily: window.getComputedStyle(e.target).fontFamily
                            })
                            e.target.style.width = `${Math.max(60, width + 20)}px`
                          }}
                          className="editable-content text-center w-full"
                          style={{
                            minWidth: '60px',
                            width: 'auto',
                            display: 'inline-block',
                            margin: '0 auto'
                          }}
                        >
                          {formatCurrency(parseFloat(getRecoveryValue('hsfo_price')) || 0)}
                        </div>}
                    </td>
                    <td className="p-2 text-center non-editable" style={{ border: '1px dashed #ccc' }}>
                      {(() => {
                        const recovery = parseFloat(getRecoveryValue('hsfo_recovery')) || 0
                        const pct_qty = parseFloat(getRecoveryValue('pct_qty')) || 0
                        const value = recovery && pct_qty ? recovery * pct_qty * 0.01 : 0
                        return value > 0 ? formatCurrency(value) : ''
                      })()}
                    </td>
                    <td className="p-2 text-center non-editable" style={{ border: '1px dashed #ccc' }}>
                      {(() => {
                        const qty = parseFloat(getRecoveryValue('hsfo_qty')) || 0
                        const mt_price = parseFloat(getRecoveryValue('mt_price')) || 0
                        const value = qty && mt_price ? qty * mt_price : 0
                        return value > 0 ? formatCurrency(value) : ''
                      })()}
                    </td>
                  </tr>
                  <tr>
                    <th className="p-2 text-left" style={{ border: '1px dashed #ccc', width: '20%', backgroundColor: '#FFF4E6' }}>MGO</th>
                    <td className="p-2 text-center" style={{ border: '1px dashed #ccc' }}>
                      {<div
                          contentEditable
                          suppressContentEditableWarning
                          onBlur={(e) => {
                            const newValue = e.target.textContent.trim()
                            handleRecoveryEdit('mgo_logbook', newValue)
                          }}
                          onInput={(e) => {
                            const text = e.target.textContent || ''
                            const width = measureTextWidth(text, {
                              fontFamily: window.getComputedStyle(e.target).fontFamily
                            })
                            e.target.style.width = `${Math.max(60, width + 20)}px`
                          }}
                          className="editable-content text-center w-full"
                          style={{
                            minWidth: '60px',
                            width: 'auto',
                            display: 'inline-block',
                            margin: '0 auto'
                          }}
                        >
                          {formatFloat(parseFloat(getRecoveryValue('mgo_logbook')) || 0)}
                        </div>}
                    </td>
                    <td className="p-2 text-center" style={{ border: '1px dashed #ccc' }}>
                      {<div
                          contentEditable
                          suppressContentEditableWarning
                          onBlur={(e) => {
                            const newValue = e.target.textContent.trim()
                            handleRecoveryEdit('mgo_survey', newValue)
                          }}
                          onInput={(e) => {
                            const text = e.target.textContent || ''
                            const width = measureTextWidth(text, {
                              fontFamily: window.getComputedStyle(e.target).fontFamily
                            })
                            e.target.style.width = `${Math.max(60, width + 20)}px`
                          }}
                          className="editable-content text-center w-full"
                          style={{
                            minWidth: '60px',
                            width: 'auto',
                            display: 'inline-block',
                            margin: '0 auto'
                          }}
                        >
                          {formatFloat(parseFloat(getRecoveryValue('mgo_survey')) || 0)}
                        </div>}
                    </td>
                    <td className="p-2 text-center" style={{ border: '1px dashed #ccc' }}>
                      {<div
                          contentEditable
                          suppressContentEditableWarning
                          onBlur={(e) => {
                            const newValue = e.target.textContent.trim()
                            handleRecoveryEdit('mgo_qty', newValue)
                          }}
                          onInput={(e) => {
                            const text = e.target.textContent || ''
                            const width = measureTextWidth(text, {
                              fontFamily: window.getComputedStyle(e.target).fontFamily
                            })
                            e.target.style.width = `${Math.max(60, width + 20)}px`
                          }}
                          className="editable-content text-center w-full"
                          style={{
                            minWidth: '60px',
                            width: 'auto',
                            display: 'inline-block',
                            margin: '0 auto'
                          }}
                        >
                          {formatFloat(parseFloat(getRecoveryValue('mgo_qty')) || 0)}
                        </div>}
                    </td>
                    <td className="p-2 text-center" style={{ border: '1px dashed #ccc' }}>
                      {<div
                          contentEditable
                          suppressContentEditableWarning
                          onBlur={(e) => {
                            const newValue = e.target.textContent.trim()
                            handleRecoveryEdit('mgo_price', newValue)
                          }}
                          onInput={(e) => {
                            const text = e.target.textContent || ''
                            const width = measureTextWidth(text, {
                              fontFamily: window.getComputedStyle(e.target).fontFamily
                            })
                            e.target.style.width = `${Math.max(60, width + 20)}px`
                          }}
                          className="editable-content text-center w-full"
                          style={{
                            minWidth: '60px',
                            width: 'auto',
                            display: 'inline-block',
                            margin: '0 auto'
                          }}
                        >
                          {formatCurrency(parseFloat(getRecoveryValue('mgo_price')) || 0)}
                        </div>}
                    </td>
                    <td className="p-2 text-center non-editable" style={{ border: '1px dashed #ccc' }}>
                      {(() => {
                        const recovery = parseFloat(getRecoveryValue('mgo_recovery')) || 0
                        const pct_qty = parseFloat(getRecoveryValue('pct_qty')) || 0
                        const value = recovery && pct_qty ? recovery * pct_qty * 0.01 : 0
                        return value > 0 ? formatCurrency(value) : ''
                      })()}
                    </td>
                    <td className="p-2 text-center non-editable" style={{ border: '1px dashed #ccc' }}>
                      {(() => {
                        const qty = parseFloat(getRecoveryValue('mgo_qty')) || 0
                        const mt_price = parseFloat(getRecoveryValue('mt_price')) || 0
                        const value = qty && mt_price ? qty * mt_price : 0
                        return value > 0 ? formatCurrency(value) : ''
                      })()}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}

          {/* Bank Details and Footer - Keep together */}
          <div className="invoice-footer-group">
          {/* Bank Details */}
          <div className="p-6 bg-gray-50 border-b border-gray-200 invoice-footer-section">
            <h6 className="text-blue-600 mb-4 font-semibold text-lg">Bank Details</h6>
            {kbl_account === 'USA' && bank_info && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 px-5 pt-5 pb-3" style={{ paddingLeft: 0 }}>
                <div className="space-y-0">
                  <div className="flex items-center justify-between py-2 border-b border-gray-200">
                    <div className="w-36 font-semibold text-gray-700 text-sm" style={{ paddingLeft: 0, marginLeft: 0 }}>Account Holder:</div>
                    <div className="text-gray-900 font-medium text-sm">{bank_info.account_holder}</div>
                  </div>
                  <div className="flex items-center justify-between py-2 border-b border-gray-200">
                    <div className="w-36 font-semibold text-gray-700 text-sm" style={{ paddingLeft: 0, marginLeft: 0 }}>Bank:</div>
                    <div className="text-gray-900 font-medium text-sm">{bank_info.bank_name}</div>
                  </div>
                  <div className="flex items-center justify-between py-2 border-b border-gray-200">
                    <div className="w-36 font-semibold text-gray-700 text-sm" style={{ paddingLeft: 0, marginLeft: 0 }}>Swift:</div>
                    <div className="text-gray-900 font-mono font-medium text-sm">{bank_info.swift}</div>
                  </div>
                  <div className="flex items-center justify-between py-2 border-b border-gray-200">
                    <div className="w-36 font-semibold text-gray-700 text-sm" style={{ paddingLeft: 0, marginLeft: 0 }}>Account # for USD:</div>
                    <div className="text-gray-900 font-mono font-medium text-sm">{bank_info.account_number}</div>
                  </div>
                  <div className="flex items-center justify-between py-2 border-b border-gray-200">
                    <div className="w-36 font-semibold text-gray-700 text-sm" style={{ paddingLeft: 0, marginLeft: 0 }}>Routing No (Wire):</div>
                    <div className="text-gray-900 font-mono font-medium text-sm">{bank_info.wire_routing}</div>
                  </div>
                  <div className="flex items-center justify-between py-2">
                    <div className="w-36 font-semibold text-gray-700 text-sm" style={{ paddingLeft: 0, marginLeft: 0 }}>Routing No (ACH/DD):</div>
                    <div className="text-gray-900 font-mono font-medium text-sm">{bank_info.ach_routing}</div>
                  </div>
                </div>
              </div>
            )}
            {kbl_account === 'UAE' && bank_info && (
              <div className="space-y-3">
                {/* Bank Information Card */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 px-5 pt-5 pb-3" style={{ paddingLeft: 0 }}>
                  <div className="space-y-0">
                    {/* Beneficiary Header */}
                    <div className="flex items-center justify-between py-1 border-b border-gray-200 mb-1">
                      <div className="w-36 font-semibold text-gray-700 text-sm" style={{ paddingLeft: 0, marginLeft: 0 }}>Beneficiary:</div>
                      <div className="flex-1 text-center text-gray-900 font-bold" style={{ fontSize: '0.75rem' }}>KBL INTERNATIONAL SERVICES LLC-FZ</div>
                    </div>
                    
                    {/* Bank Information */}
                    <div className="flex items-center justify-between py-1 border-b border-gray-200">
                      <div className="w-36 font-semibold text-gray-700 text-sm" style={{ paddingLeft: 0, marginLeft: 0 }}>Bank:</div>
                      <div className="uae-bank-cols">
                        <div className="text-gray-900 font-medium text-sm">WIO BANK PJSC</div>
                        <div className="text-gray-900 font-medium text-sm">MASHREQ BANK PSC</div>
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between py-1 border-b border-gray-200">
                      <div className="w-36 font-semibold text-gray-700 text-sm" style={{ paddingLeft: 0, marginLeft: 0 }}>Bank Address:</div>
                      <div className="uae-bank-cols">
                        <div className="text-gray-900 text-sm">Etihad Airways Centre 5th Floor, Abu Dhabi, UAE</div>
                        <div className="text-gray-900 text-sm">PO Box 1250, Dubai, UAE</div>
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between py-1">
                      <div className="w-36 font-semibold text-gray-700 text-sm" style={{ paddingLeft: 0, marginLeft: 0 }}>SWIFT:</div>
                      <div className="uae-bank-cols">
                        <div className="text-gray-900 font-mono font-medium text-sm">WIOBAEADXXX</div>
                        <div className="text-gray-900 font-mono font-medium text-sm">BOMLAEAD</div>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* USD Account Card */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 px-5" style={{ paddingLeft: 0 }}>
                  <div className="space-y-0">
                    <div className="flex items-center justify-between py-1 border-b border-gray-200">
                      <div className="w-36 font-semibold text-gray-700 text-sm" style={{ paddingLeft: 0, marginLeft: 0 }}>USD Account #:</div>
                      <div className="uae-bank-cols">
                        <div className="text-gray-900 font-mono font-medium text-sm">9269633820</div>
                        <div className="text-gray-900 font-mono font-medium text-sm">019101669474</div>
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between py-1">
                      <div className="w-36 font-semibold text-gray-700 text-sm" style={{ paddingLeft: 0, marginLeft: 0 }}>USD IBAN:</div>
                      <div className="uae-bank-cols">
                        <div className="text-gray-900 font-mono font-medium text-sm">AE51 0860 0000 0926 9633 820</div>
                        <div className="text-gray-900 font-mono font-medium text-sm">AE81 0330 0000 1910 1669 474</div>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* EUR Account Card */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 px-5" style={{ paddingLeft: 0 }}>
                  <div className="space-y-0">
                    <div className="flex items-center justify-between py-1 border-b border-gray-200">
                      <div className="w-36 font-semibold text-gray-700 text-sm" style={{ paddingLeft: 0, marginLeft: 0 }}>EUR Account #:</div>
                      <div className="uae-bank-cols">
                        <div className="text-gray-900 font-mono font-medium text-sm">9661623494</div>
                        <div className="text-gray-900 font-mono font-medium text-sm"></div>
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between py-1">
                      <div className="w-36 font-semibold text-gray-700 text-sm" style={{ paddingLeft: 0, marginLeft: 0 }}>EUR IBAN:</div>
                      <div className="uae-bank-cols">
                        <div className="text-gray-900 font-mono font-medium text-sm">AE69 0860 0000 0966 1623 494</div>
                        <div className="text-gray-900 font-mono font-medium text-sm"></div>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* AED Account Card */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 px-5" style={{ paddingLeft: 0 }}>
                  <div className="space-y-0">
                    <div className="flex items-center justify-between py-1 border-b border-gray-200">
                      <div className="w-36 font-semibold text-gray-700 text-sm" style={{ paddingLeft: 0, marginLeft: 0 }}>AED Account #:</div>
                      <div className="uae-bank-cols">
                        <div className="text-gray-900 font-mono font-medium text-sm">9539184466</div>
                        <div className="text-gray-900 font-mono font-medium text-sm">019101669473</div>
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between py-1">
                      <div className="w-36 font-semibold text-gray-700 text-sm" style={{ paddingLeft: 0, marginLeft: 0 }}>AED IBAN:</div>
                      <div className="uae-bank-cols">
                        <div className="text-gray-900 font-mono font-medium text-sm">AE26 0860 0000 0953 9184 466</div>
                        <div className="text-gray-900 font-mono font-medium text-sm">AE11 0330 0000 1910 1669 473</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Payment Reminder */}
          <div className="p-6 border-b border-gray-200 invoice-footer-section" style={{ backgroundColor: 'white' }}>
            <p className="text-red-700 text-sm text-center font-semibold mb-2">
              Please ensure the full invoice amount is transferred, with any bank fees or charges covered by the payer. Kindly send an 'Invoice Paid' email to finance.kbl@kblmarine.com with the following details: Date, Total Amount, Invoice Numbers, and Amount Paid for each invoice.
            </p>
            <p className="text-red-700 text-sm text-center font-semibold mb-2">
              A late fee of 2% per month will be applied to all overdue balances. Client is responsible for all costs of collection, including legal fees.
            </p>
            <div className="flex justify-between text-xs text-gray-600">
              <span>finance.kbl@kblmarine.com</span>
              <span>www.kblmarine.com</span>
            </div>
          </div>
          </div>
          {/* End Invoice Footer Group */}

          {/* Print Button */}
          <div className="p-6 text-center no-print invoice-footer-section">
            <button
              onClick={handlePrint}
              className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg shadow-md"
            >
              <i className="bi bi-printer mr-2"></i>
              Print Invoice
            </button>
            <button
              onClick={() => navigate('/surveys')}
              className="ml-4 px-6 py-3 bg-gray-600 hover:bg-gray-700 text-white font-semibold rounded-lg shadow-md"
            >
              Back to Surveys
            </button>
          </div>
          </div>
          {/* End Content Wrapper */}
        </div>
      </div>
    </div>
  )
}

export default Invoice
